/**
 * The SDR workspace — plan §5.1, §6.3, §15, §15.1.
 *
 * Outlook-shaped: a list rail and a reading pane, scrolling independently,
 * filling the window. The rail holds four regions:
 *
 *   Queue      — work that needs the rep. The only region with an editor.
 *   Scheduled  — approved, waiting for a send slot. The LAST state a send can
 *                be pulled back from, so Skip and Snooze stay live here.
 *   Done today — sent/completed since the rep's local midnight, with a lazy
 *                "N older" reach into history.
 *   Not sending — blocked/snoozed waits and today's skipped/stopped/expired,
 *                each stating WHY (verbatim) and WHEN it unlocks (§15).
 *
 * The board refreshes itself every sixty seconds — paused while a draft is
 * dirty or a request is in flight — so a send firing in the background visibly
 * migrates its card from Scheduled to Done rather than lying until the next
 * click. Three rules this surface holds to, all from §15: every blocked item
 * states why and when; a guard failure shows the guard's own words; nothing
 * fakes data.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { absolute,
  absoluteInZone, api, ApiError, isPast,
  isToday, peek, relative } from './api';
import { can, MERGE_TOKENS, type Role } from '@actos/shared';
import { ProspectPanel } from './ProspectPanel';
import { ComposeOneOff } from './ComposeOneOff';
import { useVendor, type VendorDescriptor } from './useVendor';
import { tip, tipAt, titleTip } from './tips';
import type { Capacity } from './Capacity';
import { useQueueActionsRef, useRegisterQueueActions } from './queueActions';
import { TASK_ROW_ATTR, useAutoAdvance, useQueueKeymap } from './useQueueKeymap';
import { PreviewFrame } from './PreviewFrame';

export interface Task {
  id: number;
  kind: string;
  stepType: string;
  stepIndex: number | null;
  status: string;
  priorityClass: string;
  promoted: boolean;
  deadline: string | null;
  visibleAt: string | null;
  updatedAt: string;
  needsReview: boolean;
  reviewReason: string | null;
  blockedReason: string | null;
  unlocksAt: string | null;
  /** When this contact last actually replied — null if never. */
  repliedAt: string | null;
  subject: string | null;
  bodyHtml: string | null;
  rationale: string | null;
  /** For a manual step this IS the task — there is no draft to review. */
  instruction: string | null;
  threadMode: string | null;
  accountId: number | null;
  recipient: {
    email: string | null;
    name: string | null;
    title: string | null;
    company: string | null;
    /**
     * The recipient's OWN zone, or null when their country is unknown or has no
     * mapping. Null means the UI says nothing about their local time — printing
     * the rep's own clock under the words "their time" is worse than omitting
     * it (see `knownTimezoneForCountry`).
     */
    timeZone: string | null;
  };
  /** Null on a research task — it has no sending identity because it sends nothing. */
  mailbox: { id: number; email: string } | null;
  sequenceName: string | null;
  targetAt: string | null;
  sendStatus: string | null;
  sentAt: string | null;
  accountScore: number | null;
  /**
   * What this person's PREVIOUS record said, when they got here by changing
   * jobs (§8.2). Present only when that record carried an opt-out or a pause —
   * a banner on every mover would train reps to stop reading it.
   */
  priorRecord: { fromCompany: string | null; fromTitle: string | null; note: string } | null;
  /**
   * The two most recent things that happened at this account (§8.2), newest
   * first. Always an array — empty is the common case and is not an error.
   */
  signals: { kind: string; value: string | null; occurredAt: string }[];
  /**
   * Where an API email came from (plans/external-send-api.md §8) — null for
   * everything the product itself creates. `mode: 'html'` means the platform
   * wrote the text, so there is nothing to regenerate.
   */
  source: { platform: string; ref: string | null; url: string | null; mode: 'html' | 'generate' } | null;
  createdAt: string;
}

interface Snapshot {
  tasks: Task[];
  older: number;
  ordering: string;
}

interface Guard {
  guard_kind: string;
  mechanism: string;
  passed: boolean;
  reason: string | null;
}

const BANDS = [
  {
    key: 'urgent',
    label: 'Urgent',
    hint: 'A deadline falls inside the promotion window, or an external platform asked for this now.',
  },
  { key: 'sdr', label: 'SDR', hint: 'One-to-one outbound.' },
  { key: 'marketing', label: 'Marketing', hint: 'Yields to SDR unless dated.' },
];

type Region = 'queue' | 'scheduled' | 'done' | 'stopped';

/**
 * Which region a task lives in — §15.1's table, as one function.
 *
 * A snoozed task is the only status that crosses regions on its own: it waits
 * in Not sending until its return date passes, then it is queue work again
 * even though nothing wrote to the row.
 */
function regionOf(task: Task): Region {
  switch (task.status) {
    case 'sent':
    case 'completed':
      return 'done';
    case 'approved':
    case 'queued':
      return 'scheduled';
    case 'pending':
    case 'ready':
      return 'queue';
    case 'snoozed': {
      const due = task.unlocksAt ?? task.visibleAt;
      return due && new Date(due).getTime() <= Date.now() ? 'queue' : 'stopped';
    }
    default:
      // blocked, skipped, stopped, expired.
      return 'stopped';
  }
}

const STATE_LABEL: Record<string, string> = {
  blocked: 'Blocked',
  snoozed: 'Snoozed',
  skipped: 'Skipped',
  stopped: 'Stopped',
  expired: 'Expired',
  sent: 'Sent',
  completed: 'Done',
};

/**
 * The mockup's per-type tones — the strongest at-a-glance cue it had.
 *
 * Names only. The hues are `--tone-*` in theme.css, beside the rest of the
 * palette, because three brand colours written as hex in a .tsx const are
 * three colours a designer editing the palette will never find. Each value is
 * handed to CSS as `--row-tone` on the row element and read back by
 * `.task-row.active` and `.row-icon`.
 *
 * `email` is the unmarked type and deliberately shares the body ink rather
 * than getting a tone of its own.
 */
const TONES: Record<string, string> = {
  email: 'var(--color-text)',
  call: 'var(--tone-call)',
  linkedin: 'var(--tone-linkedin)',
  research: 'var(--tone-research)',
};

const ROW_TIPS: Record<Region, string> = {
  queue: 'Opens this item for review in the pane alongside. Nothing sends until you approve it.',
  scheduled:
    'Opens the approved email read-only. Until its slot arrives, Skip can still pull the send back.',
  done: 'Opens what actually went out, read-only.',
  stopped: 'Opens this item read-only, with the reason it is not sending.',
};

function accountKey(task: Task): string {
  if (task.recipient.company) return task.recipient.company;
  const domain = task.recipient.email?.split('@')[1];
  return domain ?? 'No account';
}

const NO_ACCOUNT = 'No account';

/**
 * The account-chip filter's key. An API email to a personal address has no
 * account at all (plans/external-send-api.md A18): its row still reads
 * "gmail.com" so a rep can place it, but every such row groups under ONE chip
 * — a chip per free-mail provider would present gmail.com as a company.
 */
function chipKey(task: Task): string {
  return task.accountId === null && task.source ? NO_ACCOUNT : accountKey(task);
}

/**
 * A safe read-only rendering of a draft body. The UI never injects HTML — this
 * strips the markup and keeps the paragraph breaks, which for the simple
 * `<p>`-structured drafts the generator writes is the whole layout.
 */
function htmlToText(html: string): string {
  const withBreaks = html
    .replace(/<\/(p|div|h[1-6]|li|blockquote|tr)>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n');
  const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

/** The opening of a draft, flattened to one line for a scanning surface. */
function excerpt(html: string, limit: number): string {
  const text = htmlToText(html).replace(/\s+/g, ' ').trim();
  return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
}

function StepIcon({ stepType }: { stepType: string }) {
  const common = { width: 15, height: 15, viewBox: '0 0 256 256', fill: 'currentColor' } as const;
  switch (stepType) {
    case 'call':
      return (
        <svg {...common} aria-hidden>
          <path d="M222.37,158.46l-47.11-21.11-.13-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L134.87,160c-15.42-7.49-31.34-23.29-38.83-38.51l20.78-24.71c.2-.25.39-.5.57-.77a16,16,0,0,0,1.32-15.06l0-.12L97.54,33.64a16,16,0,0,0-16.62-9.52A56.26,56.26,0,0,0,32,80c0,79.4,64.6,144,144,144a56.26,56.26,0,0,0,55.88-48.92A16,16,0,0,0,222.37,158.46ZM176,208A128.14,128.14,0,0,1,48,80,40.2,40.2,0,0,1,82.87,40a.61.61,0,0,0,0,.12l21,47L83.2,111.86a6.13,6.13,0,0,0-.57.77,16,16,0,0,0-1,15.7c9.06,18.53,27.73,37.06,46.46,46.11a16,16,0,0,0,15.75-1.14,8.44,8.44,0,0,0,.74-.56L169.14,152l47,21.05h0s.08,0,.11,0A40.21,40.21,0,0,1,176,208Z" />
        </svg>
      );
    case 'linkedin':
      return (
        <svg {...common} aria-hidden>
          <path d="M216,24H40A16,16,0,0,0,24,40V216a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V40A16,16,0,0,0,216,24Zm0,192H40V40H216V216ZM96,112v64a8,8,0,0,1-16,0V112a8,8,0,0,1,16,0Zm88,28v36a8,8,0,0,1-16,0V140a20,20,0,0,0-40,0v36a8,8,0,0,1-16,0V112a8,8,0,0,1,15.79-1.78A36,36,0,0,1,184,140ZM100,84A12,12,0,1,1,88,72,12,12,0,0,1,100,84Z" />
        </svg>
      );
    case 'research':
      return (
        <svg {...common} aria-hidden>
          <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
        </svg>
      );
    case 'email':
      return (
        <svg {...common} aria-hidden>
          <path d="M224,48H32a8,8,0,0,0-8,8V192a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A8,8,0,0,0,224,48Zm-96,85.15L52.57,64H203.43ZM98.71,128,40,181.81V74.19Zm11.84,10.85L122.05,149.4a8,8,0,0,0,11.9,0l11.5-10.55L204.4,192H51.6ZM157.29,128,216,74.18V181.82Z" />
        </svg>
      );
    default:
      return (
        <svg {...common} aria-hidden>
          <path d="M205.66,101.66l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L144,140.69l50.34-50.35a8,8,0,0,1,11.32,11.32ZM216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,160H40V56H216V200Z" />
        </svg>
      );
  }
}

/**
 * How long an optimistic approve outranks a poll that disagrees with it — R7.
 *
 * The same fifteen seconds the draft-content poll race uses, and the same
 * argument: a snapshot request in flight when the approve committed comes back
 * carrying the OLD status, and adopting it would silently un-approve a card the
 * rep has already dealt with. Fifteen seconds is far longer than the round trip
 * and far shorter than the sixty-second poll, so at most one poll is ever
 * shielded and a genuinely stale patch cannot survive to the next one.
 */
export const APPROVAL_GRACE_MS = 15_000;

/**
 * Reconcile a fresh snapshot against approvals this tab has already made.
 *
 * Exported and pure so the race can be tested directly — there is no DOM test
 * environment here, so a decision left inside the component is a decision with
 * no coverage at all.
 *
 * Three cases, and the middle one is the whole point:
 *   - the row already says something OTHER than what it said before the
 *     approve: the server knows something newer than we do (it sent, a reply
 *     stopped it), so the server wins and the entry is done with;
 *   - the row still says what it said before AND the grace window is open: this
 *     snapshot predates our approve, so the approval is re-asserted;
 *   - the grace window has closed: we stop insisting. If the server still
 *     disagrees after fifteen seconds, the approve did not take, and pretending
 *     otherwise would leave a card permanently lying about its state.
 *
 * `settled` is returned rather than mutating the caller's map, so the function
 * has no side effects to reason about at the call site.
 */
export function reconcileApprovedTasks(
  tasks: Task[],
  pending: ReadonlyMap<number, { previousStatus: string; at: number; targetAt?: string | null }>,
  now: number,
): { tasks: Task[]; settled: number[] } {
  const settled: number[] = [];
  let changed = false;

  const next = tasks.map((task) => {
    const entry = pending.get(task.id);
    if (!entry) return task;

    if (task.status !== entry.previousStatus || now - entry.at > APPROVAL_GRACE_MS) {
      settled.push(task.id);
      return task;
    }

    /*
     * The SLOT travels with the status. A snapshot that predates the approve
     * carries `targetAt: null`, and re-asserting only the status would leave
     * the card reading "Approved — waiting for the scheduler to assign a slot"
     * about a task that was handed one in the response this shield exists to
     * defend. Restoring half the answer is a worse lie than restoring none.
     */
    changed = true;
    return { ...task, status: 'approved', targetAt: entry.targetAt ?? task.targetAt };
  });

  return { tasks: changed ? next : tasks, settled };
}

/** The rep's local midnight — "today" is their day, not the server's (§15.1). */
function localMidnightIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/**
 * The top-of-day strip — §15.1's "Today", and R5 of the SDR backlog.
 *
 * Four numbers, in the order a rep acts on them: what came back overnight,
 * what needs them now, what is already committed, what is stuck. Everything
 * here is a count of rows that are on this screen anyway — the strip's job is
 * to spare the scroll, not to introduce a second source of truth.
 *
 * The last line is R8: what today has produced so far. It is deliberately not a
 * dashboard. A rep wants to know they are moving; a page of charts is a
 * manager's question and belongs somewhere a rep does not have to walk past.
 *
 * A tile with nothing in it renders as "—" rather than "0". Zero is a
 * measurement and reads as one; on an empty morning queue a row of zeroes looks
 * like a broken fetch, which is exactly the confusion §15's "nothing here fakes
 * data" rule is about.
 */
function Briefing({
  counts,
  unreviewedReplies,
  onOpenReplies,
  filtered,
}: {
  counts: {
    toApprove: number;
    scheduled: number;
    blocked: number;
    sentToday: number;
    repliedToday: number;
  };
  unreviewedReplies: number;
  onOpenReplies: () => void;
  /** True when an account chip is active, so the strip can say what it counts. */
  filtered: boolean;
}) {
  const show = (n: number) => (n === 0 ? '—' : String(n));

  return (
    <div className="briefing">
      <div className="briefing-tiles">
        <button
          type="button"
          className="briefing-tile"
          {...tip(
            'Switches to the Replies tab, where inbound mail and what ActOS did about it can be corrected. Switching views sends nothing and loses no work.',
          )}
          onClick={onOpenReplies}
        >
          <span className="briefing-n">{show(unreviewedReplies)}</span>
          <span className="briefing-label">to review</span>
        </button>
        <div
          className="briefing-tile"
          {...tip('Drafts and manual steps waiting on you. This is the top lane of the queue.')}
        >
          <span className="briefing-n">{show(counts.toApprove)}</span>
          <span className="briefing-label">to approve</span>
        </div>
        <div
          className="briefing-tile"
          {...tip(
            'Approved and holding a send slot. Skip and Snooze still work on these — a slot is the last point a send can be pulled back.',
          )}
        >
          <span className="briefing-n">{show(counts.scheduled)}</span>
          <span className="briefing-label">scheduled</span>
        </div>
        <div
          className="briefing-tile"
          {...tip(
            'Waiting on something — a guard, a research step, a snooze. Each one states why in its row. Skipped and stopped work is not counted here: that is a decision someone already made.',
          )}
        >
          <span className="briefing-n">{show(counts.blocked)}</span>
          <span className="briefing-label">waiting</span>
        </div>
      </div>
      <p className="fine briefing-done">
        {`Today: ${counts.sentToday} sent · ${counts.repliedToday} replied`}
        {filtered ? ' · counting the selected accounts only' : ''}
      </p>
    </div>
  );
}

export function Workspace({
  role,
  onShowHelp,
  autoAdvance,
  unreviewedReplies,
  onOpenReplies,
  capacity,
  followUp,
  onFollowUpConsumed,
}: {
  /** The signed-in user's role — decides whether "Send anyway" is offered. */
  role: Role;
  onShowHelp: () => void;
  /** Owned by the SDR area, so the shortcut overlay's toggle drives this too. */
  autoAdvance: boolean;
  /**
   * Owned by the SDR area too, because the tab badge needs it whether or not
   * the queue is mounted. Passed in rather than fetched again here — a second
   * poll of `/replies` to render one number is exactly the cost §4 is trying to
   * take out of this screen.
   */
  unreviewedReplies: number;
  onOpenReplies: () => void;
  /**
   * Polled once by the SDR area and shared (R11). Null until the first fetch
   * lands, and every consumer must render as though it may never arrive —
   * capacity is context beside Approve, never a gate on it.
   */
  capacity: Capacity | null;
  /** A follow-up seeded from a reply — R13. Opens the compose box when set. */
  followUp: { to: string; brief: string } | null;
  onFollowUpConsumed: () => void;
}) {
  /*
   * Seeded from whatever this tab last saw on this path, so returning to the
   * queue from Replies or Accounts paints the board immediately and checks it
   * behind the paint — the same bargain `useQuery` makes for the list surfaces.
   *
   * Seeded rather than converted to the hook, and deliberately: this board owns
   * optimistic approvals, a reconcile against them, a busy guard and a dirty
   * guard, none of which a generic read hook knows about. The one thing it
   * needed was the first frame, and that is a `peek`.
   *
   * The raw cached body, NOT a reconciled one. `reconcileApprovals` re-asserts
   * approvals this tab has made that the server's snapshot predates, and on a
   * fresh mount there are none to re-assert — `pendingApprovals` starts empty.
   * Running it here would be ceremony over an empty map.
   *
   * The key carries `localMidnightIso()` exactly as the fetch does, so the seed
   * can only ever be today's board. A rep whose tab was open across midnight
   * gets a key nothing has cached, misses, and waits — which is correct: what
   * was cached is yesterday.
   */
  const [snapshot, setSnapshot] = useState<Snapshot | null>(
    () => peek<Snapshot>(`/tasks?since=${encodeURIComponent(localMidnightIso())}`) ?? null,
  );
  const [history, setHistory] = useState<Task[]>([]);
  const [historyNext, setHistoryNext] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filter, setFilter] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  /**
   * Quick-approve takes over the reading pane rather than opening a modal: the
   * rail stays where it is, the account filter keeps applying, and the widest
   * area on screen is the one showing the subject lines being decided on.
   */
  const [quickApprove, setQuickApprove] = useState(false);

  /*
   * A follow-up handed over from the Replies tab opens the compose box — R13.
   *
   * The seed is consumed AS IT IS TAKEN, not when the box closes. Held until
   * close, it survives this component unmounting — which is what a tab switch
   * to Replies does — and re-fires on the way back, reopening the compose box
   * over whatever the rep had since typed and reseeding it from a reply they
   * dealt with ten minutes ago. Copied into local state so the box still has
   * its values after the hand-off is cleared.
   */
  const [composeSeed, setComposeSeed] = useState<{ to: string; brief: string } | null>(null);

  useEffect(() => {
    if (!followUp) return;
    setComposeSeed(followUp);
    setComposing(true);
    onFollowUpConsumed();
  }, [followUp, onFollowUpConsumed]);

  // Poll guards, as refs so the interval closure always reads the truth. A
  // refresh landing mid-edit would clobber the draft being typed — the one
  // way a self-updating board can destroy work.
  const dirtyRef = useRef(false);
  const busyRef = useRef(false);
  const hasLoadedRef = useRef(false);

  /**
   * Approvals this tab has made but has not yet seen come back — R7.
   *
   * Approving used to leave the card sitting in the queue until a full refetch
   * completed, which at a busy moment is a second of a rep looking at a row
   * they have already dealt with and wondering whether the click registered.
   * The server already answers with everything needed to move it, and the
   * answer was being thrown away.
   *
   * The entry is the rollback record AND the poll shield. It holds the previous
   * status so a refusal can put the card back exactly where it was, and the
   * time it was made so a poll response that left the server BEFORE the approve
   * committed — carrying `status: 'ready'` — cannot silently un-approve a card
   * on screen. That race is real and already solved once in this file for draft
   * content; this is the same shape with the same 15-second grace, deliberately,
   * rather than a second mechanism that has to be reasoned about separately.
   */
  const pendingApprovals = useRef(
    new Map<
      number,
      {
        previousStatus: string;
        previousTargetAt: string | null;
        /** The slot the server assigned, re-asserted alongside the status. */
        targetAt: string | null;
        at: number;
      }
    >(),
  );

  /**
   * The snapshot as a ref, so an optimistic patch can read the current board
   * synchronously. Written inside `applySnapshot` rather than in an effect: a
   * patch fired from a click handler must see the board as it is now, not as it
   * was at the last commit.
   */
  const snapshotRef = useRef<Snapshot | null>(null);

  const applySnapshot = useCallback((data: Snapshot) => {
    snapshotRef.current = data;
    const doc = document as Document & { startViewTransition?: (cb: () => void) => void };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (doc.startViewTransition && hasLoadedRef.current && !reduced) {
      // The migration moment: a card that changed region glides there instead
      // of teleporting. flushSync so the DOM change happens inside the
      // transition's capture window.
      doc.startViewTransition(() => flushSync(() => setSnapshot(data)));
    } else {
      setSnapshot(data);
    }
    hasLoadedRef.current = true;
  }, []);

  /**
   * Re-assert an approval the server has confirmed but this snapshot predates.
   *
   * Only while the grace window is open, and only over the exact status the
   * card had before — if the row has moved on to anything else (sent, stopped
   * by a reply that just landed) the server is telling us something newer than
   * we know, and it wins. An entry whose row has caught up is dropped, so the
   * map drains itself rather than shielding forever.
   */
  const reconcileApprovals = useCallback((data: Snapshot): Snapshot => {
    const pending = pendingApprovals.current;
    if (pending.size === 0) return data;

    const { tasks, settled } = reconcileApprovedTasks(data.tasks, pending, Date.now());
    for (const id of settled) pending.delete(id);
    return tasks === data.tasks ? data : { ...data, tasks };
  }, []);

  const load = useCallback(async () => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const data = await api<Snapshot>(`/tasks?since=${encodeURIComponent(localMidnightIso())}`);
      applySnapshot(reconcileApprovals(data));
      setError(null);
    } catch (e) {
      /*
       * The snapshot is left ALONE. It used to fall back to an empty board —
       * `{ tasks: [], older: 0, ordering: '' }` — which is a positive claim
       * that there is no work today, made at the exact moment the app has no
       * idea. Everything derived from it went along with the story: the
       * account chips emptied, the briefing strip read "— — — —", and the
       * board looked like a finished morning rather than a failed request.
       *
       * Null on a first failure means the render below shows the error and a
       * retry and nothing else. A failed REFRESH keeps the board that is
       * already on screen, which is the right outcome for a rep mid-queue.
       */
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      busyRef.current = false;
    }
  }, [applySnapshot, reconcileApprovals]);

  /**
   * Move the card now, on the server's own word.
   *
   * Called with what `POST /tasks/:id/approve` actually returned, not with a
   * guess — the send slot on screen is the slot the scheduler assigned. Goes
   * through `applySnapshot` so the card GLIDES from Queue to Scheduled; setting
   * state directly would skip the view transition and teleport it, which
   * defeats the point of moving it early.
   */
  const markApproved = useCallback(
    (id: number, targetAt: string | null) => {
      const current = snapshotRef.current;
      const task = current?.tasks.find((t) => t.id === id);
      if (!current || !task) return;

      pendingApprovals.current.set(id, {
        previousStatus: task.status,
        previousTargetAt: task.targetAt,
        targetAt: targetAt ?? task.targetAt,
        at: Date.now(),
      });

      applySnapshot({
        ...current,
        tasks: current.tasks.map((t) =>
          t.id === id ? { ...t, status: 'approved', targetAt: targetAt ?? t.targetAt } : t,
        ),
      });
    },
    [applySnapshot],
  );

  /**
   * Put it back, exactly as it was.
   *
   * A refusal restores the status AND the send slot — a card left showing a
   * slot it does not hold reads as approved-but-broken, which is worse than the
   * refusal it is reporting. The button's own guard state is restored by the
   * caller, which is the only thing that knows why the refusal happened.
   */
  /**
   * Forget the shield for approvals that have been UNDONE — R3's undo.
   *
   * Without this the undo is invisible for fifteen seconds. The shield exists
   * to re-assert `approved` over a poll that still says `ready`, and an undo
   * makes the server say `ready` again on purpose — which is exactly the state
   * the shield was built to override. The card would snap back to Scheduled and
   * the rep would watch their undo undo itself.
   */
  const forgetApprovals = useCallback((ids: number[]) => {
    for (const id of ids) pendingApprovals.current.delete(id);
  }, []);

  const rollbackApproval = useCallback(
    (id: number) => {
      const entry = pendingApprovals.current.get(id);
      pendingApprovals.current.delete(id);
      const current = snapshotRef.current;
      if (!entry || !current) return;

      applySnapshot({
        ...current,
        tasks: current.tasks.map((t) =>
          t.id === id ? { ...t, status: entry.previousStatus, targetAt: entry.previousTargetAt } : t,
        ),
      });
    },
    [applySnapshot],
  );

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      if (document.hidden || dirtyRef.current) return;
      void load();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const loadOlder = useCallback(async () => {
    const before = historyNext ?? localMidnightIso();
    try {
      const data = await api<{ tasks: Task[]; nextBefore: string | null }>(
        `/tasks/history?before=${encodeURIComponent(before)}`,
      );
      setHistory((prev) => [...prev, ...data.tasks]);
      setHistoryNext(data.nextBefore);
      setHistoryLoaded(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [historyNext]);

  const tasks = useMemo(() => snapshot?.tasks ?? [], [snapshot]);

  const selected = useMemo(
    () => tasks.find((t) => t.id === selectedId) ?? history.find((t) => t.id === selectedId) ?? null,
    [tasks, history, selectedId],
  );

  /** Account chips are derived from the snapshot, counts included. */
  const accounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of tasks) {
      const key = chipKey(task);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [tasks]);

  const passes = useCallback(
    (task: Task) => filter.length === 0 || filter.includes(chipKey(task)),
    [filter],
  );

  const regions = useMemo(() => {
    const byRegion: Record<Region, Task[]> = { queue: [], scheduled: [], done: [], stopped: [] };
    for (const task of tasks) {
      if (!passes(task)) continue;
      byRegion[regionOf(task)].push(task);
    }
    const time = (v: string | null) => (v ? new Date(v).getTime() : Number.MAX_SAFE_INTEGER);
    byRegion.scheduled.sort((a, b) => time(a.targetAt) - time(b.targetAt));
    byRegion.done.sort(
      (a, b) =>
        new Date(b.sentAt ?? b.updatedAt).getTime() - new Date(a.sentAt ?? a.updatedAt).getTime(),
    );
    byRegion.stopped.sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
    return byRegion;
  }, [tasks, passes]);

  const banded = useMemo(() => {
    const groups = new Map<string, Task[]>();
    for (const task of regions.queue) {
      const band = task.promoted ? 'urgent' : task.priorityClass;
      groups.set(band, [...(groups.get(band) ?? []), task]);
    }
    return groups;
  }, [regions.queue]);

  /**
   * The top-of-day summary — §15.1's "Today", made countable.
   *
   * The header said "Today" over a flat list, which is a label rather than a
   * briefing: a rep opening the queue at 09:00 could not tell whether they had
   * four things to do or forty, whether anything was blocked, or whether
   * something had come back overnight, without scrolling the whole rail and
   * counting.
   *
   * Derived entirely from the snapshot already on screen — no second request,
   * nothing added to the 60s poll. That is also why it is computed from the
   * FILTERED regions: with an account chip active the rail shows one account,
   * and a strip quoting environment-wide numbers above it would be describing a
   * different screen. Replies are the exception and are marked as such — they
   * live on the other tab and no account filter reaches them.
   *
   * `blocked` counts only what is genuinely waiting on something (blocked or
   * snoozed), not everything in Not sending: a skipped or stopped task is a
   * decision someone already made, and counting those as work would make the
   * number grow all day and mean nothing by the afternoon.
   */
  const briefing = useMemo(() => {
    const waiting = regions.stopped.filter(
      (task) => task.status === 'blocked' || task.status === 'snoozed',
    ).length;

    const sentToday = regions.done.filter((task) => task.status === 'sent').length;

    /*
     * People, not rows. One reply stops a whole sequence, so the same
     * `repliedAt` is stamped on every remaining step of it — counting task rows
     * turned one prospect answering into "6 replied" and made the number worse
     * the longer the cadence was. Keyed on the recipient, which is the closest
     * thing to a contact id the snapshot carries.
     */
    const repliedToday = new Set(
      regions.done
        .concat(regions.scheduled, regions.queue, regions.stopped)
        .filter((task) => task.repliedAt && isToday(task.repliedAt))
        .map((task) => task.recipient.email ?? `task-${task.id}`),
    ).size;

    return {
      toApprove: regions.queue.length,
      scheduled: regions.scheduled.length,
      blocked: waiting,
      sentToday,
      repliedToday,
    };
  }, [regions]);

  const olderVisible = history.filter(passes);
  const olderRemaining = (snapshot?.older ?? 0) - history.length;

  /*
   * The rail as one flat list, for J/K — §15.1.
   *
   * Built by walking exactly what the render below walks, in the same order:
   * BANDS over the queue lane first (a band missing from BANDS is grouped but
   * never rendered, so iterating `banded`'s own keys would visit rows that are
   * not on screen), then the three lanes, then Older. Anything that diverges
   * here sends the keyboard to a row the eye cannot find.
   */
  const order = useMemo(() => {
    const ids: number[] = [];
    for (const band of BANDS) {
      for (const task of banded.get(band.key) ?? []) ids.push(task.id);
    }
    for (const lane of [regions.scheduled, regions.done, regions.stopped]) {
      for (const task of lane) ids.push(task.id);
    }
    for (const task of history) {
      if (passes(task)) ids.push(task.id);
    }
    return ids;
  }, [banded, regions, history, passes]);

  const advance = useAutoAdvance({
    order,
    selectedId,
    onSelect: setSelectedId,
    enabled: autoAdvance,
  });

  useQueueKeymap({
    order,
    selectedId,
    onSelect: setSelectedId,
    actions: useQueueActionsRef(),
    onShowHelp,
  });

  return (
    <section className="workspace">
      <div className="queue-rail">
        <header className="rail-head">
          <p className="kicker">Queue</p>
          <div className="rail-title">
            <h1>Today</h1>
            <div className="rail-title-actions">
              {regions.queue.some(batchEligible) && (
                <button
                  type="button"
                  className={quickApprove ? 'quick-btn active' : 'quick-btn'}
                  aria-pressed={quickApprove}
                  {...tip(
                    'Opens a list of the drafts that passed every guard, to approve several at once. Anything flagged for review is not offered here — those are read one at a time. Nothing sends immediately either way.',
                  )}
                  onClick={() => setQuickApprove((v) => !v)}
                >
                  {quickApprove ? 'Back to queue' : 'Quick approve'}
                </button>
              )}
              <button
                type="button"
                className="compose-btn"
                {...tip(
                  'Writes a one email to one person, outside any sequence. If they are not in HubSpot yet, approving the send creates them there.',
                )}
                onClick={() => setComposing(true)}
              >
                New email
              </button>
            </div>
          </div>
          {snapshot?.ordering && <p className="why">{snapshot.ordering}</p>}
        </header>

        {snapshot && (
          <Briefing
            counts={briefing}
            unreviewedReplies={unreviewedReplies}
            onOpenReplies={onOpenReplies}
            filtered={filter.length > 0}
          />
        )}

        {composing && (
          <ComposeOneOff
            key={composeSeed ? `followup-${composeSeed.to}` : 'blank'}
            initialTo={composeSeed?.to ?? ''}
            initialBrief={composeSeed?.brief ?? ''}
            onClose={() => {
              setComposing(false);
              setComposeSeed(null);
            }}
            onCreated={async (taskId) => {
              setComposing(false);
              // The task exists and is drafted — pull it onto the board and
              // open it, so "write the draft" lands the rep in the editor.
              await load();
              setSelectedId(taskId);
              setComposeSeed(null);
            }}
          />
        )}

        {accounts.length > 1 && (
          <div className="rail-chips">
            {accounts.map(([name, count]) => (
              <button
                key={name}
                type="button"
                className={filter.includes(name) ? 'chip active' : 'chip'}
                {...tip(
                  filter.includes(name)
                    ? 'Stops filtering by this account.'
                    : 'Shows only this account, in every section below. Click again to clear.',
                )}
                onClick={() =>
                  setFilter((prev) =>
                    prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
                  )
                }
              >
                {name} <span className="chip-count">{count}</span>
              </button>
            ))}
            {filter.length > 0 && (
              <button
                type="button"
                className="chip chip-clear"
                {...tip('Clears the account filter and shows the whole feed again.')}
                onClick={() => setFilter([])}
              >
                All accounts
              </button>
            )}
          </div>
        )}

        {/*
          Error before the loading line, and answered with a way out. Shown
          together they contradicted each other; shown with no retry, the only
          recovery from a dropped request was reloading the whole app.
        */}
        {error && (
          <>
            <p className="error-note">{error}</p>
            <div className="draft-actions">
              <button
                type="button"
                {...tip('Asks for the queue again. Nothing is approved, skipped or sent by trying.')}
                onClick={() => void load()}
              >
                Try again
              </button>
            </div>
          </>
        )}
        {snapshot === null && !error && <p className="why">Loading&hellip;</p>}

        {snapshot !== null && tasks.length === 0 && !error && (
          <div className="empty-state">
            <p className="why">
              Nothing queued. Enroll a contact in an approved sequence to fill this — step 1 is
              drafted on the next tick.
            </p>
          </div>
        )}

        {BANDS.map((band) => {
          const items = banded.get(band.key) ?? [];
          if (!items.length) return null;
          return (
            <div key={band.key} className="band">
              <p className="band-label">
                {band.label} <span className="band-count">{items.length}</span>
              </p>
              <p className="fine">{band.hint}</p>
              <div className="lane-body">
                {items.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    active={selectedId === task.id}
                    onSelect={() => setSelectedId(task.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}

        <Lane
          title="Scheduled"
          count={regions.scheduled.length}
          hint="Approved and waiting for a send slot. Skip still pulls one back."
          tipText="Approved work the scheduler will send at its assigned slot. Open an item to see when — or to pull it back."
          selectedId={selectedId}
          tasks={regions.scheduled}
          onSelect={setSelectedId}
        />

        <Lane
          title="Done today"
          count={regions.done.length}
          hint="Sent or completed since midnight."
          tipText="What went out or was completed today. Rows open read-only — the record of what actually happened."
          selectedId={selectedId}
          tasks={regions.done}
          onSelect={setSelectedId}
        />

        <Lane
          title="Not sending"
          count={regions.stopped.length}
          hint="Each row states why, verbatim, and when it unlocks."
          tipText="Work that is paused, blocked or ended — with the reason stated. Nothing here sends without a human acting first."
          selectedId={selectedId}
          tasks={regions.stopped}
          onSelect={setSelectedId}
        />

        {(olderRemaining > 0 || olderVisible.length > 0) && (
          <div className="lane">
            <p className="band-label">Older</p>
            {olderVisible.length > 0 && (
              <div className="lane-body">
                {olderVisible.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    active={selectedId === task.id}
                    onSelect={() => setSelectedId(task.id)}
                  />
                ))}
              </div>
            )}
            {(olderRemaining > 0 || (historyLoaded && historyNext !== null)) && (
              <button
                type="button"
                className="older-btn"
                {...tip(
                  'Loads earlier executed work — fifty rows at a time, read-only. Nothing here can send.',
                )}
                onClick={() => void loadOlder()}
              >
                Show {historyLoaded ? 'more' : `${olderRemaining} older`}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="draft-rail">
        {quickApprove ? (
          <QuickApprove
            tasks={regions.queue}
            onDone={load}
            onClose={() => setQuickApprove(false)}
            onOptimisticApprove={markApproved}
            onForgetApprovals={forgetApprovals}
          />
        ) : selected ? (
          <Detail
            key={selected.id}
            task={selected}
            role={role}
            onChanged={load}
            onApproved={advance}
            onDirty={(dirty) => {
              dirtyRef.current = dirty;
            }}
            onOptimisticApprove={markApproved}
            onRollbackApprove={rollbackApproval}
            capacity={capacity}
          />
        ) : (
          <div className="detail">
            <p className="why">Select an item to review it.</p>
          </div>
        )}
      </div>
    </section>
  );
}

function Lane({
  title,
  hint,
  count,
  tipText,
  tasks,
  selectedId,
  onSelect,
}: {
  title: string;
  hint: string;
  count: number;
  tipText: string;
  tasks: Task[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}) {
  if (count === 0) return null;
  return (
    <details className="lane" open>
      <summary {...tip(tipText)}>
        {title} <span className="band-count">{count}</span>
        <span className="lane-hint">{hint}</span>
      </summary>
      <div className="lane-body">
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            active={selectedId === task.id}
            onSelect={() => onSelect(task.id)}
          />
        ))}
      </div>
    </details>
  );
}

/**
 * The signal vocabulary, in a rep's words rather than the database's.
 *
 * Same map as the account drawer's, deliberately duplicated rather than shared:
 * these two surfaces have different room. `champion_departed` is the one that
 * matters most and reads worst raw — the person who was going to buy has left,
 * which is either the end of the account or the start of a new one somewhere
 * else, and neither is legible from an underscore-separated identifier.
 */
const SIGNAL_LABEL: Record<string, string> = {
  meeting_booked: 'Meeting booked',
  champion_departed: 'Contact left',
  champion_arrived: 'Contact joined',
  role_changed: 'Changed role',
  external_score: 'Scored externally',
};

/** The most a single batch may carry — mirrors MAX_BATCH_APPROVE on the API. */
const QUICK_APPROVE_LIMIT = 20;

interface BatchOutcome {
  id: number;
  ok: boolean;
  targetAt?: string;
  error?: string;
  reason?: string;
}

/**
 * Is this draft one a batch may carry? — R3.
 *
 * A client-side mirror of the API's eligibility rules, and deliberately only a
 * mirror: `POST /tasks/approve-batch` re-checks every one of these against the
 * database and refuses per task with its own reason. This exists so the list a
 * rep sees is the list that will actually go through, not so the browser can
 * decide anything.
 *
 * `needsReview` covers both of the cases that must never be batched — a failed
 * content guard and the §6 coverage advisory — because the snapshot carries the
 * flag but not the individual guard verdicts. That makes this filter slightly
 * stricter than the server's, which is the right direction for it to be wrong
 * in: a draft wrongly kept out of the batch is one the rep opens and approves
 * normally, and nothing has been sent that they did not read.
 */
export function batchEligible(task: Task): boolean {
  return (
    task.stepType === 'email' &&
    task.status !== 'blocked' &&
    Boolean(task.bodyHtml) &&
    Boolean(task.sequenceName) &&
    !task.needsReview
  );
}

/**
 * Approve a screenful of unremarkable drafts at once — R3 of the SDR backlog.
 *
 * Rendered in the READING PANE rather than the rail, which is the whole reason
 * this is usable: the rail is capped at 480px and a checkbox list there would
 * be a column of truncated subjects, while the pane is the widest thing on
 * screen and the subject line is what a rep is actually deciding on.
 *
 * What it is not: a way to send without reading. Every row shows the recipient,
 * the subject and the step, nothing is ticked by default, and anything carrying
 * a failed guard or a review flag never appears here at all — those are opened
 * one at a time, which is what §6.1 asks for.
 */
function QuickApprove({
  tasks,
  onDone,
  onClose,
  onOptimisticApprove,
  onForgetApprovals,
}: {
  /** The queue lane, already account-filtered — the rep batches what they see. */
  tasks: Task[];
  onDone: () => Promise<void>;
  onClose: () => void;
  onOptimisticApprove: (id: number, targetAt: string | null) => void;
  /** Drop the optimistic shield for ids an undo has just put back. */
  onForgetApprovals: (ids: number[]) => void;
}) {
  const eligible = useMemo(() => tasks.filter(batchEligible), [tasks]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<BatchOutcome[] | null>(null);
  const [undone, setUndone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const approvedIds = (outcomes ?? []).filter((o) => o.ok).map((o) => o.id);
  const refused = (outcomes ?? []).filter((o) => !o.ok);

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < QUICK_APPROVE_LIMIT) next.add(id);
      return next;
    });

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ results: BatchOutcome[] }>('/tasks/approve-batch', {
        method: 'POST',
        body: JSON.stringify({ ids: [...picked] }),
      });
      setOutcomes(result.results);
      // Each card moves on the server's own answer, exactly as a single
      // approve does — the batch is a different number of clicks, not a
      // different contract.
      for (const outcome of result.results) {
        if (outcome.ok) onOptimisticApprove(outcome.id, outcome.targetAt ?? null);
      }
      setPicked(new Set());
      void onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ undone: number[]; alreadyGone: number[]; reason: string | null }>(
        '/tasks/undo-approvals',
        { method: 'POST', body: JSON.stringify({ ids: approvedIds }) },
      );
      // Before the reload, so the snapshot that comes back is not overridden by
      // a shield that is now describing a state the rep has cancelled.
      onForgetApprovals(approvedIds);
      setUndone(
        result.alreadyGone.length === 0
          ? `Put ${result.undone.length} back in the queue. Nothing was sent.`
          : `Put ${result.undone.length} back. ${result.alreadyGone.length} could not be undone — ${result.reason ?? 'they had already gone.'}`,
      );
      setOutcomes(null);
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="detail">
      <p className="kicker">Quick approve</p>
      <h2>Approve several at once</h2>
      <p className="why">
        Only drafts that passed every guard and carry no review flag appear here. Anything the
        machine wanted a person to look at is opened one at a time, on purpose. Approving assigns a
        send slot &mdash; it does not send, and each one can still be pulled back.
      </p>

      {error && <p className="error-note">{error}</p>}
      {undone && <p className="ok-note">{undone}</p>}

      {outcomes && (
        <div className="batch-result">
          <p className="ok-note">
            {approvedIds.length === 0
              ? 'Nothing was approved.'
              : `Approved ${approvedIds.length}. They are scheduled, not sent.`}
          </p>
          {refused.length > 0 && (
            <ul className="batch-refusals">
              {refused.map((outcome) => (
                <li key={outcome.id} className="fine">
                  <strong>#{outcome.id}</strong> — {outcome.reason ?? outcome.error}
                </li>
              ))}
            </ul>
          )}
          <div className="draft-actions">
            {approvedIds.length > 0 && (
              <button
                type="button"
                disabled={busy}
                {...tip(
                  'Cancels the send slots this batch just assigned and puts those steps back in the queue. Anything that has already gone cannot be recalled, and will be named.',
                )}
                onClick={() => void undo()}
              >
                {busy ? 'Undoing…' : `Undo these ${approvedIds.length}`}
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              {...tip('Closes this and returns to the queue. Nothing further is approved.')}
              onClick={onClose}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {!outcomes && eligible.length === 0 && (
        <p className="why">
          Nothing in the queue can be batched right now. Drafts still being generated, steps that
          are not email, and anything flagged for review are all handled one at a time.
        </p>
      )}

      {!outcomes && eligible.length > 0 && (
        <>
          <ul className="batch-list">
            {eligible.map((task) => (
              <li key={task.id} className="batch-row">
                <label className="batch-check">
                  <input
                    type="checkbox"
                    checked={picked.has(task.id)}
                    disabled={busy || (!picked.has(task.id) && picked.size >= QUICK_APPROVE_LIMIT)}
                    onChange={() => toggle(task.id)}
                  />
                  <span className="batch-lines">
                    <span className="batch-subject">{task.subject ?? '(no subject)'}</span>
                    <span className="fine">
                      {[
                        task.recipient.name ?? task.recipient.email,
                        accountKey(task),
                        task.sequenceName,
                        task.stepIndex ? `step ${task.stepIndex}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {/*
                      The opening of the actual email, not just its subject.

                      §6.1's "approval per touch, always" is about a human
                      deciding on each message, and a list that showed only a
                      subject line would be asking them to decide on the one
                      part the writer is least likely to get wrong. The first
                      couple of sentences are where a bad draft announces
                      itself. Tag-stripped through `htmlToText`, keeping the
                      no-innerHTML rule; the full body is one click away in the
                      normal pane, which is where anything doubtful should go.
                    */}
                    {task.bodyHtml && (
                      <span className="batch-preview">{excerpt(task.bodyHtml, 180)}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <div className="draft-actions is-pinned">
            <button
              type="button"
              className="primary"
              disabled={busy || picked.size === 0}
              {...tip(
                'Approves every ticked draft and gives each one its own send slot, spaced by the same pacing rules a single approval uses. Nothing leaves immediately.',
              )}
              onClick={() => void submit()}
            >
              {busy ? 'Approving…' : `Approve ${picked.size || ''}`.trim()}
            </button>
            <button
              type="button"
              disabled={busy}
              {...tip('Leaves everything as it is and returns to the queue.')}
              onClick={onClose}
            >
              Cancel
            </button>
            <p className="fine">
              {picked.size >= QUICK_APPROVE_LIMIT
                ? `${QUICK_APPROVE_LIMIT} is the most one batch carries. Approve these, then pick the rest.`
                : `${eligible.length} can be batched · ${QUICK_APPROVE_LIMIT} at a time`}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function TaskRow({
  task,
  active,
  onSelect,
}: {
  task: Task;
  active: boolean;
  onSelect: () => void;
}) {
  const region = regionOf(task);
  const tone = TONES[task.stepType] ?? 'var(--color-neutral-500)';

  /*
   * The score only appears when there is nothing better to say. It is a number
   * a rep can neither check nor act on — "score 71" never told anyone why an
   * account was worth their morning — so where the account has actual signals
   * the chips below replace it, and the number moves into their tooltip.
   */
  const chips = region === 'queue' ? task.signals.slice(0, 2) : [];

  const meta =
    region === 'scheduled'
      ? task.targetAt
        ? isPast(task.targetAt)
          ? // The slot came; the next scheduler pass (≤20 min away) sends it.
            'sending shortly'
          : `sends ${relative(task.targetAt)}`
        : 'awaiting slot'
      : region === 'done'
        ? relative(task.sentAt ?? task.updatedAt)
        : region === 'stopped'
          ? relative(task.updatedAt)
          : chips.length === 0 && task.accountScore !== null
            ? `score ${task.accountScore}`
            : '';

  const subject =
    task.subject ??
    task.instruction ??
    (task.stepType === 'email' ? 'Draft not generated yet' : `${task.stepType} step`);

  const showState = region === 'stopped' || (region === 'done' && task.status === 'completed');

  return (
    <button
      type="button"
      className={active ? 'task-row active' : 'task-row'}
      /* The keymap's Space guard and its scroll target both key off this. */
      {...{ [TASK_ROW_ATTR]: task.id }}
      style={{ '--row-tone': tone, viewTransitionName: `task-${task.id}` } as React.CSSProperties}
      {...tip(ROW_TIPS[region])}
      onClick={onSelect}
    >
      <span className="row-icon">
        <StepIcon stepType={task.stepType} />
      </span>
      <span className="row-main">
        <span className="row-top">
          <strong>{accountKey(task)}</strong>
          <span className="row-meta">{meta}</span>
        </span>
        <span className="row-subject">{subject}</span>
        <span className="row-sub">
          {showState && (
            <span className="state-chip">{STATE_LABEL[task.status] ?? task.status}</span>
          )}
          <span className="row-subtext">
            {[
              task.recipient.name ?? task.recipient.email,
              // No sequence + an email step = the compose box's one-off. Its
              // kind is 'sdr' (so reply-stops sweep it), which would render
              // here as a cryptic "sdr". An API email is the same one-off
              // from elsewhere, and says where — same slot, same type size,
              // so the row's height and scanning rhythm do not change.
              task.sequenceName ??
                (task.source
                  ? `via ${task.source.platform}`
                  : task.stepType === 'email' && task.stepIndex === null
                    ? 'one-off'
                    : task.kind),
              task.stepIndex ? `step ${task.stepIndex}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </span>
        {chips.length > 0 && (
          <span
            className="signal-chips"
            {...tip(
              task.accountScore !== null
                ? `What happened at this account recently, newest first. Its ranking score is ${task.accountScore}. Opening the account shows the full timeline; nothing here changes by looking.`
                : 'What happened at this account recently, newest first. Opening the account shows the full timeline; nothing here changes by looking.',
            )}
          >
            {chips.map((signal) => (
              <span
                key={`${signal.kind}-${signal.occurredAt}`}
                className={`signal-chip signal-${signal.kind.replace(/_/g, '-')}`}
              >
                {SIGNAL_LABEL[signal.kind] ?? signal.kind}
                {signal.value ? ` · ${signal.value}` : ''}
              </span>
            ))}
          </span>
        )}
        {task.needsReview && region === 'queue' && (
          <span className="flag flag-review">Needs review</span>
        )}
        {task.blockedReason && region === 'stopped' && (
          <span className="flag flag-blocked">
            {task.blockedReason}
            {task.unlocksAt ? ` — unlocks ${relative(task.unlocksAt)}` : ''}
          </span>
        )}
        {task.repliedAt &&
          region === 'done' &&
          // Only a reply that POSTDATES this send. A contact who answered a
          // previous sequence months ago must not stamp "Replied" onto a
          // fresh send nobody has answered yet.
          (!task.sentAt || new Date(task.repliedAt) >= new Date(task.sentAt)) && (
            /**
             * The reply belongs on THIS row, not only on its stopped
             * siblings. This is the email that was answered; without the flag
             * it reads as "sent, no response" while the rows beneath it
             * explain a stop this row appears to know nothing about.
             */
            <span className="flag flag-replied">
              Replied {relative(task.repliedAt)} — sequence stopped, see Replies
            </span>
          )}
      </span>
    </button>
  );
}

/**
 * The reading pane. Queue items get the editor (or the manual-step panel);
 * everything else is the read-only record — with Skip and Snooze kept live on
 * Scheduled, because that is the last exit before a real email leaves.
 */
interface InboxCheck {
  checked: boolean;
  debounced?: boolean;
  failure?: string | null;
  reason?: string;
  ingested?: number;
  changed?: boolean;
  status?: string;
  blockedReason?: string | null;
  latestInbound?: string | null;
  latestInboundAt?: string | null;
  latestInboundNote?: string | null;
  /** M-CON-1 — the real verdict, not parsed out of latestInboundNote's prose. */
  latestInboundHardStop?: boolean;
}

/**
 * Sweep this contact's inbox the moment the rep opens the task — §7.3, §15.1.
 *
 * The five-minute tick already stops a SEND to someone who has replied. What
 * it cannot do is stop a rep from picking up the phone. A call or LinkedIn
 * step has no send-time recheck because it has no send, so for those this is
 * the only thing standing between a fresh reply and a rep acting against it.
 *
 * Deliberately not silent on failure: "checked, nothing new" and "could not
 * check" must never look the same, because the rep is about to act either way.
 */
function useInboxCheck(taskId: number, onChanged: () => Promise<void>): InboxCheck | 'checking' {
  const [state, setState] = useState<InboxCheck | 'checking'>('checking');

  useEffect(() => {
    let live = true;
    setState('checking');

    void api<InboxCheck>(`/tasks/${taskId}/check-inbox`, { method: 'POST' })
      .then(async (result) => {
        if (!live) return;
        setState(result);
        // A consequence landed on this very task. Reload so the board moves it
        // out of the queue rather than leaving a stopped task looking actionable.
        if (result.changed) await onChanged();
      })
      .catch((e: unknown) => {
        if (!live) return;
        setState({
          checked: false,
          failure: e instanceof Error ? e.message : 'The reply check did not run.',
        });
      });

    return () => {
      live = false;
    };
  }, [taskId, onChanged]);

  return state;
}

function InboxCheckLine({ state }: { state: InboxCheck | 'checking' }) {
  if (state === 'checking') return <p className="fine">Checking for new replies…</p>;

  if (state.changed) {
    return (
      <p className="advisory-note">
        <strong>Stopped while you were opening it.</strong>{' '}
        {state.blockedReason ?? 'A reply arrived and the sequence was stopped.'} Nothing further
        will go out to this contact.
      </p>
    );
  }

  if (state.failure) {
    return (
      <p className="advisory-note">
        <strong>Not checked.</strong> {state.failure} Treat anything below as possibly out of date.
      </p>
    );
  }

  if (!state.checked) return <p className="fine">{state.reason}</p>;

  /**
   * The rep's question is "has this person replied?" — NOT "did this
   * particular check ingest anything new?".
   *
   * Those diverge the moment a reply is more than one check old: every later
   * check correctly ingests nothing, and the old fallthrough then said "No
   * new replies as of a moment ago" directly above a thread with a reply on
   * file. Technically true, and exactly wrong — it reads as "this prospect
   * has not replied". The reply state must win over the sweep report.
   */
  if (state.latestInbound === 'real_reply') {
    const when = state.latestInboundAt
      ? ` ${new Date(state.latestInboundAt).toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`
      : '';
    return (
      <p className="advisory-note">
        <strong>This prospect replied{when ? ` on${when}` : ''}.</strong>{' '}
        {state.latestInboundNote ?? 'The sequence was stopped.'} The reply itself is in the Replies
        tab. Checked again just now — nothing newer.
      </p>
    );
  }

  if (state.latestInbound === 'unsubscribe_request') {
    return (
      <p className="advisory-note">
        <strong>This contact asked to stop hearing from us.</strong>{' '}
        {state.latestInboundNote ?? 'The address was suppressed.'}{' '}
        {/*
          M-CON-1 — conditional on the actual outcome. The blanket "do not
          contact by another channel" was right for the flagged-jurisdiction
          hard stop and wrong for the common case, where SDR is only paused
          and resumable — telling a rep never to reach this person again, when
          nothing said that, is the kind of overcaution that trains people to
          stop reading the banner at all.
        */}
        {state.latestInboundHardStop
          ? 'Do not contact them by another channel either.'
          : 'Marketing has stopped; sales outreach is paused and can be resumed once someone reviews it.'}
      </p>
    );
  }

  if (state.latestInbound === 'bounce') {
    return (
      <p className="advisory-note">
        <strong>The last send to this address bounced.</strong>{' '}
        {state.latestInboundNote ?? ''}
      </p>
    );
  }

  if (state.ingested) {
    return (
      <p className="fine">
        Checked just now — {state.ingested} new message{state.ingested === 1 ? '' : 's'} read, none
        affecting this task.
      </p>
    );
  }

  // Nothing inbound from this contact, ever — the one case where "no new
  // replies" and "no reply exists" genuinely coincide.
  return <p className="fine">No new replies as of a moment ago.</p>;
}

function Detail({
  task,
  role,
  onChanged,
  onApproved,
  onDirty,
  onOptimisticApprove,
  onRollbackApprove,
  capacity,
}: {
  task: Task;
  role: Role;
  onChanged: () => Promise<void>;
  /** Auto-advance, fired only after an approve actually lands. */
  onApproved: () => void;
  onDirty: (dirty: boolean) => void;
  onOptimisticApprove: (id: number, targetAt: string | null) => void;
  onRollbackApprove: (id: number) => void;
  capacity: Capacity | null;
}) {
  const region = regionOf(task);
  const check = useInboxCheck(task.id, onChanged);

  const body =
    task.kind === 'job_change' && region === 'queue' ? (
      <JobChangeView task={task} onChanged={onChanged} />
    ) : region === 'queue' ? (
      <Draft
        task={task}
        role={role}
        onChanged={onChanged}
        onApproved={onApproved}
        onDirty={onDirty}
        onOptimisticApprove={onOptimisticApprove}
        onRollbackApprove={onRollbackApprove}
        capacity={capacity}
      />
    ) : (
      <ReadView task={task} region={region} onChanged={onChanged} />
    );

  return (
    <>
      <div className="inbox-check">
        <InboxCheckLine state={check} />
        {/*
          The previous record, said out loud. A job change starts a clean
          record by design (§8.2); an unexplained clean slate on someone who
          opted out last year is indistinguishable from a bug.
        */}
        {task.priorRecord && <p className="advisory-note">{task.priorRecord.note}</p>}
      </div>
      {/*
        Mounted here rather than inside `Draft`, so the conversation is there
        for a scheduled or sent task too — "what did we already say to this
        person" is not a question that stops mattering once a draft is approved.
      */}
      {/*
        Only where a thread can exist. A research task has no correspondent —
        the endpoint answers 422 for exactly that reason — so offering "Thread
        so far" on one is a control that can only ever disappoint. Checked
        before rendering rather than after fetching, so it never appears at all.
      */}
      {task.recipient.email && <ThreadStrip taskId={task.id} />}
      {body}
    </>
  );
}

interface ThreadMessage {
  direction: 'sent' | 'received';
  at: string;
  subject: string | null;
  opened?: number;
  clicked?: number;
  classification?: string | null;
  consequence?: string | null;
  inboundEventId?: number;
}

interface ThreadPayload {
  contactId: number;
  messages: ThreadMessage[];
  /** The real conversation in Gmail, or null when nothing has been sent yet. */
  gmailUrl: string | null;
  counts: { sent: number; opened: number; replies: number };
  note: string;
}

/**
 * The conversation so far — §15's "thread-so-far", R4 of the SDR backlog.
 *
 * A rep approving step three is otherwise deciding blind: the draft says
 * "following up" and the only way to see what it is following up ON was to
 * leave for the Replies tab, losing the draft, the scroll and the thread of
 * attention. Every competitor puts this beside the compose box.
 *
 * Collapsed to one line by default, because most of the time the summary IS the
 * answer — "3 sent · 2 opened · 1 reply" tells a rep whether this person has
 * been listening without costing them a screen. Expanding is the exception, so
 * the fetch is deferred until it happens: a strip nobody opens should not add a
 * request to every task open.
 *
 * Absent entirely for a task with no correspondent (the 422 branch). A research
 * task can never have a thread, and an empty strip promising one is a small lie
 * repeated on every row.
 */
function ThreadStrip({ taskId }: { taskId: number }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ThreadPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [absent, setAbsent] = useState(false);

  // Collapse and forget on every task change, or the strip would show the
  // previous contact's conversation under this one's draft for a moment —
  // which is the worst possible moment to be showing the wrong correspondence.
  useEffect(() => {
    setOpen(false);
    setData(null);
    setError(null);
    setAbsent(false);
  }, [taskId]);

  useEffect(() => {
    if (!open || data || absent) return;
    let live = true;

    void api<ThreadPayload>(`/tasks/${taskId}/thread`)
      .then((result) => {
        if (live) setData(result);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof ApiError && e.code === 'no_recipient') setAbsent(true);
        else setError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      live = false;
    };
  }, [open, data, absent, taskId]);

  if (absent) return null;

  const summary = data
    ? [
        `${data.counts.sent} sent`,
        data.counts.opened > 0 ? `${data.counts.opened} opened` : null,
        data.counts.replies > 0
          ? `${data.counts.replies} ${data.counts.replies === 1 ? 'reply' : 'replies'}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Thread so far';

  return (
    <div className="thread-strip">
      <button
        type="button"
        className="link-btn"
        aria-expanded={open}
        {...tip(
          'Shows what has already gone to this person and what came back. Reading only — nothing is sent, marked or changed by opening it.',
        )}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide the thread' : `Thread so far${data ? ` — ${summary}` : ''}`}
      </button>

      {open && error && (
        <p className="error-note">The conversation could not be loaded — {error}</p>
      )}

      {open && !data && !error && <p className="fine">Loading the thread&hellip;</p>}

      {open && data && data.messages.length === 0 && (
        <p className="fine">Nothing has gone to this person yet. This would be the first touch.</p>
      )}

      {open && data && data.messages.length > 0 && (
        <>
          <ol className="thread-list">
            {/* Oldest first once expanded: a conversation reads downwards, even
                though the summary above counts the newest. */}
            {[...data.messages].reverse().map((message) => (
              <li
                key={`${message.direction}-${message.at}-${message.inboundEventId ?? ''}`}
                className={`thread-item thread-${message.direction}`}
              >
                <span className="thread-when">{relative(message.at)}</span>
                <span className="thread-what">
                  <strong>{message.direction === 'sent' ? 'Sent' : 'They replied'}</strong>
                  {message.subject ? ` · ${message.subject}` : ''}
                </span>
                <span className="thread-meta">
                  {message.direction === 'sent'
                    ? [
                        message.opened ? `opened ${message.opened}×` : null,
                        message.clicked ? `clicked ${message.clicked}×` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'no opens recorded'
                    : [message.classification, message.consequence].filter(Boolean).join(' · ')}
                </span>
              </li>
            ))}
          </ol>
          <p className="fine">
            {data.note}
            {data.gmailUrl && (
              <>
                {' '}
                {/*
                  §15's "View thread". ActOS does not rebuild an email client
                  (§17.1) — the whole conversation, attachments and all, is in
                  the rep's own inbox, and the honest answer to "show me
                  everything" is to send them there rather than to reimplement
                  Gmail badly.
                */}
                <a
                  href={data.gmailUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...tip(
                    'Opens the real conversation in Gmail, in a new tab, signed in as the mailbox that sent it. Nothing here changes.',
                  )}
                >
                  Open the thread in Gmail
                </a>
              </>
            )}
          </p>
        </>
      )}
    </div>
  );
}

// ---- Job changes (§8.2) --------------------------------------------------

interface JobChange {
  transitionId: number;
  status: string;
  person: { contactId: number; name: string; email: string; linkedinUrl: string | null };
  from: { accountId: number; companyName: string | null; companyDomain: string | null; title: string | null };
  to: { companyName: string | null; companyDomain: string | null; title: string | null; positionStartDate: string | null };
  evidence: { reason: string | null; corroborated: boolean; caveat: string; vendor?: string };
  priorRecord: {
    salesOptOut: boolean;
    marketingOptOut: boolean;
    salesPaused: boolean;
    optedOutAt: string | null;
    note: string | null;
  };
  blocked: string | null;
  crmLinked: boolean;
  /**
   * The vendor adopt will reveal through — the CURRENT dial, because a reveal
   * needs only a LinkedIn URL or name + domain. `pricing` is optional so a
   * payload carrying only `displayName` still renders.
   */
  vendor?: {
    key: string;
    displayName: string;
    connected?: boolean;
    reason?: string | null;
    pricing?: { reveal: string };
  };
}

/**
 * One research task: somebody moved — §8.2, §17.5.
 *
 * The detection was cheap and automatic. Everything on this screen that costs
 * money costs it because a human pressed a button, which is the same rule
 * prospecting runs under: a reveal is never a side effect.
 */
function JobChangeView({ task, onChanged }: { task: Task; onChanged: () => Promise<void> }) {
  const [data, setData] = useState<JobChange | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [updateCrm, setUpdateCrm] = useState(true);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    void api<JobChange>(`/job-changes/by-task/${task.id}`)
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [task.id]);

  const act = async (path: string, body: unknown) => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await api<{ email?: string; company?: string; crm?: { reason: string | null } }>(
        `/job-changes/${data!.transitionId}/${path}`,
        { method: 'POST', body: JSON.stringify(body) },
      );
      setResult(
        outcome.email
          ? `Created ${outcome.email} at ${outcome.company}.${outcome.crm?.reason ? ` ${outcome.crm.reason}` : ''}`
          : 'Dismissed.',
      );
      await onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <div className="detail"><p className="advisory-note">{error}</p></div>;
  if (!data) return <div className="detail"><p className="why">Loading…</p></div>;

  return (
    <div className="detail">
      <DetailHead task={task} />

      <div className="job-change">
        <div className="job-change-move">
          <div>
            <p className="fine">Was</p>
            <p className="job-change-where">{data.from.companyName ?? data.from.companyDomain ?? '—'}</p>
            <p className="fine">{data.from.title ?? 'role unknown'}</p>
          </div>
          <span aria-hidden="true" className="job-change-arrow">→</span>
          <div>
            <p className="fine">Now</p>
            <p className="job-change-where">{data.to.companyName ?? data.to.companyDomain ?? '—'}</p>
            <p className="fine">{data.to.title ?? 'role unknown'}</p>
          </div>
        </div>

        {data.evidence.reason && <p className="why">{data.evidence.reason}</p>}

        {/* Never presented as fact. This is what a public profile says today. */}
        <p className="fine">
          {data.evidence.caveat}
          {!data.evidence.corroborated && ' No start date accompanies the change, so it is weaker evidence than usual.'}
        </p>

        {data.priorRecord.note && <p className="advisory-note">{data.priorRecord.note}</p>}

        {data.blocked ? (
          <p className="advisory-note">
            <strong>Cannot be adopted.</strong> {data.blocked}
          </p>
        ) : result ? (
          <p className="advisory-note">{result}</p>
        ) : (
          <>
            <label className="job-change-crm">
              <input
                type="checkbox"
                checked={updateCrm}
                disabled={!data.crmLinked}
                onChange={(e) => setUpdateCrm(e.target.checked)}
              />{' '}
              {data.crmLinked
                ? 'Also update their address and company in HubSpot'
                : 'No linked HubSpot record to update'}
            </label>

            <div className="actions">
              <button
                type="button"
                disabled={busy || data.vendor?.connected === false}
                {...(data.vendor?.connected === false && data.vendor.reason
                  ? tip(data.vendor.reason)
                  : {})}
                onClick={() => void act('adopt', { updateCrm: updateCrm && data.crmLinked })}
              >
                Find their new address & add them
              </button>
              <button
                type="button"
                className="ghost"
                disabled={busy}
                onClick={() => void act('dismiss', { reason: 'Not worth pursuing.' })}
              >
                Dismiss
              </button>
            </div>
            {data.vendor?.connected === false && data.vendor.reason && (
              <p className="advisory-note">{data.vendor.reason}</p>
            )}
            <p className="fine">
              {data.vendor
                ? `Adding them asks ${data.vendor.displayName} for their new address.${data.vendor.pricing ? ` ${data.vendor.pricing.reveal}` : ' That reveal is billed.'}`
                : 'Adding them reveals one address, which is billed.'}{' '}
              Their previous record keeps its own history and consent — this creates a second,
              separate contact at the new company.
            </p>
          </>
        )}

        {error && <p className="advisory-note">{error}</p>}
      </div>
    </div>
  );
}

/** The shared header: who, what, and the route it takes. */
function DetailHead({ task, statusLine }: { task: Task; statusLine?: string }) {
  return (
    <header className="detail-head">
      <p className="kicker">
        {/* A research task has no sending identity, so it is described by who
            it is about rather than by a route that does not exist. */}
        {task.mailbox
          ? `${task.mailbox.email} → ${task.recipient.email}`
          : `About ${task.recipient.email ?? task.recipient.name ?? 'this account'}`}
      </p>
      {/*
        A drafted email has a subject; before that, the step's brief stands in.
        The brief is authored text and so has no guaranteed length — a step
        angle once held a paragraph of prompt instructions and rendered here as
        a five-line title. Validation now rejects that at authorship, but the
        headline should not depend on authors behaving: label the stand-in and
        let CSS clamp it, so the worst case is a truncated brief rather than a
        wall of text where a subject belongs.
      */}
      {task.subject ? (
        <h2>{task.subject}</h2>
      ) : task.instruction ? (
        <h2 className="detail-brief" title={task.instruction}>
          <span className="brief-label">Brief</span>
          {task.instruction}
        </h2>
      ) : (
        <h2>{task.stepType} step</h2>
      )}
      <p className="fine detail-meta">
        {[
          task.recipient.name,
          task.recipient.title,
          task.recipient.company,
          task.sequenceName
            ? `${task.sequenceName}${task.stepIndex ? ` · step ${task.stepIndex}` : ''}`
            : task.stepType === 'email' && task.stepIndex === null
              ? 'One-off email'
              : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {task.source && <SourceLine task={task} source={task.source} />}
      {statusLine && <p className="status-line">{statusLine}</p>}
    </header>
  );
}

/**
 * Where an API email came from — plans/external-send-api.md §8. One line:
 * the platform, the caller's own reference, when it arrived, and a way back
 * to the run that sent it. The link's destination is whatever the caller
 * supplied, so its host is printed beside it rather than hidden behind a word.
 */
function SourceLine({ task, source }: { task: Task; source: NonNullable<Task['source']> }) {
  let host: string | null = null;
  try {
    host = source.url ? new URL(source.url).host : null;
  } catch {
    host = null;
  }

  return (
    <p
      className="fine source-line"
      {...tip(
        source.mode === 'html'
          ? `${source.platform} supplied this email's text. You can edit it; there is nothing to regenerate.`
          : `${source.platform} asked ActOS to write this email from its instructions — the brief above.`,
      )}
    >
      Submitted by {source.platform}
      {source.ref ? ` · ref ${source.ref}` : ''} · {relative(task.createdAt)}
      {source.url && host && (
        <>
          {' · '}
          <a href={source.url} target="_blank" rel="noopener noreferrer">
            open in {source.platform}
          </a>{' '}
          <span className="fine">({host})</span>
        </>
      )}
    </p>
  );
}

const TOKEN_SOURCE_LABEL: Record<string, string> = {
  contact: 'Contact',
  account: 'Company',
  sender: 'Sender',
  // {{video_link}} — resolves only on steps that attach a video.
  step: 'Step',
};

/**
 * Insert-a-token — the searchable field list for the Edit view.
 *
 * Search covers the token name, its label and its source ("company" finds
 * every account field), and every row shows THIS recipient's live value, so
 * the choice is made knowing what will actually render. An empty field is
 * not hidden — it is marked, and inserting it produces `{{token|}}` with the
 * caret in the fallback slot, because the guard will demand one.
 */
function TokenPicker({
  values,
  onInsert,
}: {
  values: Record<string, string | null> | null;
  onInsert: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const matches = MERGE_TOKENS.filter(
    (t) =>
      !q ||
      t.name.includes(q) ||
      t.label.toLowerCase().includes(q) ||
      t.source.includes(q) ||
      (TOKEN_SOURCE_LABEL[t.source] ?? '').toLowerCase().includes(q),
  );

  if (!open) {
    return (
      <div className="draft-actions">
        <button
          type="button"
          {...tip(
            'Opens the searchable list of contact, company and sender fields. Picking one inserts {{its_token}} at your cursor; it resolves per recipient at send time.',
          )}
          onClick={() => setOpen(true)}
        >
          Insert token
        </button>
      </div>
    );
  }

  return (
    <div className="token-picker">
      <div className="token-picker-head">
        <input
          autoFocus
          placeholder="Search fields — first name, company, industry…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          {...titleTip('Filters the field list. Matching works on the field name, its label and where it comes from.')}
        />
        <button type="button" className="link-btn" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      {matches.length === 0 && (
        <p className="fine">No field matches “{query}”. Fields come from the contact, their company and the sending mailbox.</p>
      )}

      <ul className="token-list">
        {matches.map((t) => {
          const value = values?.[t.name] ?? null;
          const loaded = values !== null;
          return (
            <li key={t.name}>
              <button
                type="button"
                className="token-row"
                // onMouseDown, so the click does not first blur the field the
                // caret is in — the insert needs that field's selection.
                onMouseDown={(e) => {
                  e.preventDefault();
                  onInsert(t.name);
                }}
              >
                <code>{`{{${t.name}}}`}</code>
                <span className="token-label">
                  {t.label} · {TOKEN_SOURCE_LABEL[t.source]}
                </span>
                <span className={value ? 'token-value' : 'token-value token-empty'}>
                  {!loaded
                    ? '…'
                    : value
                      ? `→ ${value}`
                      : t.minted
                        ? 'nothing to link to yet — this one cannot take a fallback'
                        : 'empty — inserts with a fallback slot'}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="fine">
        Tokens resolve per recipient at send time. An empty field needs a fallback —{' '}
        <code>{'{{first_name|there}}'}</code> — or the Tokens guard will hold the draft.
      </p>
    </div>
  );
}

/** A row in the vidshare picker — served by GET /library/videos. */
interface PickableVideo {
  id: string;
  slug: string;
  title: string;
  hasThumb?: boolean;
  /** Short-lived presigned preview for THIS list UI; emails use the stable URL the mint returns. */
  thumbPreviewUrl?: string | null;
}

/**
 * Insert-a-video — the vidshare library, in the Edit view.
 *
 * Picking a video is not a text insertion: the parent immediately asks the
 * video tool to mint THIS recipient's personal link (so the rep sees the real
 * URL on the spot), records the pick on the task, and drops {{video_embed}}
 * into the body — which renders as a clickable thumbnail in the sent email.
 */
function VideoPicker({
  onPick,
  busy,
  attached,
  onRemove,
}: {
  onPick: (v: PickableVideo) => void;
  busy: boolean;
  attached: { title: string; url: string } | null;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [videos, setVideos] = useState<PickableVideo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || videos !== null) return;
    let cancelled = false;
    api<{ videos: PickableVideo[] }>('/library/videos')
      .then((d) => {
        if (!cancelled) setVideos(d.videos);
      })
      .catch((e) => {
        if (cancelled) return;
        setVideos([]);
        setLoadError(
          e instanceof ApiError && e.message.includes('not_configured')
            ? 'The video library is not connected.'
            : 'The video library is unreachable right now.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, videos]);

  if (attached) {
    return (
      <div className="draft-actions">
        <span className="fine">
          🎥 <strong>{attached.title}</strong> — link created for this contact:{' '}
          <a href={attached.url} target="_blank" rel="noreferrer">
            {attached.url.replace(/^https?:\/\//, '')}
          </a>
        </span>
        <button
          type="button"
          className="link-btn"
          {...tip(
            "Detaches the video from this email and is safe to click: the recipient's link itself stays valid (an admin revokes links from the video tool). Remember to delete the {{video_embed}} placeholder from the body too.",
          )}
          onClick={onRemove}
        >
          Remove video
        </button>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="draft-actions">
        <button
          type="button"
          {...tip(
            "Opens the video library (PMG Video Tool). Picking one immediately creates this contact's personal tracked link and inserts a clickable thumbnail block at your cursor.",
          )}
          onClick={() => setOpen(true)}
        >
          Insert video
        </button>
      </div>
    );
  }

  return (
    <div className="token-picker">
      <div className="token-picker-head">
        <span className="fine" style={{ alignSelf: 'center' }}>
          {videos === null ? 'Loading videos…' : 'Pick a video — the link is generated for this contact.'}
        </span>
        <button type="button" className="link-btn" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      {loadError && <p className="fine">{loadError}</p>}
      {videos !== null && videos.length === 0 && !loadError && (
        <p className="fine">No ready videos in the library yet — upload or import one in the video tool first.</p>
      )}

      <ul className="token-list">
        {(videos ?? []).map((v) => (
          <li key={v.slug}>
            <button
              type="button"
              className="token-row"
              disabled={busy}
              // onMouseDown, so the click does not first blur the body field —
              // the {{video_embed}} insert needs its caret position.
              onMouseDown={(e) => {
                e.preventDefault();
                if (!busy) {
                  setOpen(false);
                  onPick(v);
                }
              }}
            >
              {v.thumbPreviewUrl ? (
                <img
                  src={v.thumbPreviewUrl}
                  alt=""
                  style={{ width: 56, height: 32, objectFit: 'cover', borderRadius: 4, flex: '0 0 auto' }}
                />
              ) : (
                <span style={{ width: 56, textAlign: 'center', flex: '0 0 auto' }}>🎥</span>
              )}
              <span className="token-label">{v.title}</span>
              <span className={v.hasThumb ? 'token-value' : 'token-value token-empty'}>
                {v.hasThumb ? 'thumbnail ready' : 'no thumbnail — will insert a text link'}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <p className="fine">
        The pick lands as <code>{'{{video_embed}}'}</code> — a clickable thumbnail per recipient. The guard
        holds any draft that drops it, and the link is unique to this contact, so their views are tracked.
      </p>
    </div>
  );
}

interface PreviewPayload {
  subject: string;
  html: string;
  from: { email: string; name: string | null };
  to: { email: string; name: string | null };
  tokens: { name: string; value: string | null }[];
  unresolved: string[];
  note: string;
}

/**
 * The rendered email — §15's "compose + preview", served by the same code
 * path that sends (`/tasks/:id/preview`). The iframe is sandboxed with no
 * permissions at all: the body is model-written HTML and gets to render, not
 * to run.
 */
function PreviewPane({ taskId, refreshKey }: { taskId: number; refreshKey: string }) {
  const [data, setData] = useState<PreviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    api<PreviewPayload>(`/tasks/${taskId}/preview`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [taskId, refreshKey]);

  if (error) return <p className="error-note">Preview unavailable: {error}</p>;
  if (!data) return <p className="fine">Rendering preview&hellip;</p>;

  const mergedTokens = data.tokens.filter((t) => t.value);

  return (
    <div className="preview-pane">
      <p className="fine">
        <strong>From:</strong> {data.from.name ? `${data.from.name} <${data.from.email}>` : data.from.email}
        {' · '}
        <strong>To:</strong> {data.to.name ? `${data.to.name} <${data.to.email}>` : data.to.email}
      </p>
      <p className="fine">
        <strong>Subject:</strong> {data.subject}
      </p>

      {data.unresolved.length > 0 && (
        <p className="error-note">
          {data.unresolved.map((t) => `{{${t}}}`).join(', ')} cannot be resolved for this recipient
          — the send will be refused until the field is filled or the token gets a fallback.
        </p>
      )}

      <PreviewFrame html={data.html} />

      {mergedTokens.length > 0 && (
        <p className="fine">
          Merged for this recipient:{' '}
          {mergedTokens.map((t) => `{{${t.name}}} → ${t.value}`).join(' · ')}
        </p>
      )}
      <p className="fine">{data.note}</p>
    </div>
  );
}

interface EvidencePayload {
  facts: { label: string; value: string; source: string }[];
  model: string | null;
  promptVersion: string | null;
  policyMode: string | null;
  at: string | null;
  note: string | null;
}

/**
 * What the writer actually knew — R9 of the SDR backlog.
 *
 * The single highest-value thing on that list, and the reason is §6.1's own
 * north star: "the AI writes genuinely personalised emails, and the SDR's job
 * is review, not rewrite." Review is only possible if the claims in a draft can
 * be CHECKED. With one sentence of rationale and nothing behind it, the fastest
 * way for a rep to be sure a sentence is true is to write the sentence
 * themselves — which is how a review surface quietly turns back into a writing
 * surface.
 *
 * Every fact is tagged with where it came from, because that is what makes it
 * checkable rather than merely visible: "Industry — logistics (CRM)" tells a
 * rep exactly which record to distrust when the email reads wrong.
 *
 * Collapsed by default and fetched only when opened. Most drafts do not need
 * checking, and a panel that costs a request on every task open would tax the
 * common case to serve the rare one.
 */
function Evidence({ taskId }: { taskId: number }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<EvidencePayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Forget on every task change, or an open panel would show the previous
  // draft's facts under this one's copy.
  useEffect(() => {
    setOpen(false);
    setData(null);
    setError(null);
  }, [taskId]);

  useEffect(() => {
    if (!open || data) return;
    let live = true;
    void api<EvidencePayload>(`/tasks/${taskId}/evidence`)
      .then((result) => {
        if (live) setData(result);
      })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [open, data, taskId]);

  return (
    <div className="evidence">
      <button
        type="button"
        className="link-btn"
        aria-expanded={open}
        {...tip(
          'Shows the facts the writer was given and where each one came from, so a claim in the draft can be checked without leaving the queue. Reading only.',
        )}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide what it knew' : 'What did it know?'}
      </button>

      {open && error && <p className="error-note">{error}</p>}
      {open && !data && !error && <p className="fine">Loading&hellip;</p>}

      {open && data && data.facts.length === 0 && (
        <p className="fine">
          {data.note === 'no_generation'
            ? 'No generation is on record for this task, so there is nothing to show. Manual steps never had one.'
            : 'The writer was given no account facts — this draft rests on the sequence angle and your org profile alone. That is worth knowing before approving it.'}
        </p>
      )}

      {open && data && data.facts.length > 0 && (
        <>
          <dl className="meta-list evidence-list">
            {data.facts.map((fact) => (
              <div key={`${fact.label}-${fact.source}`} className="meta-row">
                <dt>
                  {fact.label} <span className="evidence-source">{fact.source}</span>
                </dt>
                <dd>{fact.value}</dd>
              </div>
            ))}
          </dl>
          <p className="fine">
            Written by {data.model ?? 'the configured writer'}
            {data.at ? ` ${relative(data.at)}` : ''}. These are the facts it was given — not
            everything known about the account, and not a guarantee it used them all.
          </p>
        </>
      )}
    </div>
  );
}

/** The side column: everything worth knowing that is not the draft itself. */
function TaskMeta({ task }: { task: Task }) {
  const rows: [string, string][] = [];
  if (task.accountScore !== null) rows.push(['Account score', String(task.accountScore)]);
  // An API email to a personal address has no company to score (A18).
  else if (task.accountId === null && task.source) rows.push(['Account', 'none (personal address)']);
  if (task.sequenceName)
    rows.push(['Sequence', `${task.sequenceName}${task.stepIndex ? ` · step ${task.stepIndex}` : ''}`]);
  if (task.threadMode)
    rows.push([
      'Threading',
      task.threadMode === 'reply' ? 'Reply in the existing conversation' : 'New thread',
    ]);
  /*
   * Their clock first, then the rep's.
   *
   * The slot was chosen to land inside the RECIPIENT'S working day (§5.2), so
   * the recipient's local time is the number that explains the choice; the
   * rep's own is context. Shown in that order for the same reason, and only
   * when the two actually differ — "9:00 AM their time · 9:00 AM your time" is
   * noise dressed as precision.
   */
  if (task.targetAt) {
    const theirs = absoluteInZone(task.targetAt, task.recipient.timeZone);
    const mine = absolute(task.targetAt);
    rows.push([
      'Send slot',
      theirs && theirs !== mine ? `${theirs} their time · ${mine} your time` : mine,
    ]);
  }
  if (task.deadline) rows.push(['Deadline', absolute(task.deadline)]);

  return (
    <aside className="detail-side">
      {task.rationale && (
        <div className="rationale">
          <p className="variance-title">Why this angle</p>
          <p className="fine">{task.rationale}</p>
        </div>
      )}
      <Evidence taskId={task.id} />
      {rows.length > 0 && (
        <dl className="meta-list">
          {rows.map(([label, value]) => (
            <div key={label} className="meta-row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </aside>
  );
}

function ReadView({
  task,
  region,
  onChanged,
}: {
  task: Task;
  region: Region;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setMessage(null);
    try {
      await fn();
      await onChanged();
      setMessage({ kind: 'ok', text: `${label} done.` });
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };

  const skip = () =>
    act('Skip', () =>
      api(`/tasks/${task.id}/skip`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Pulled back before sending.' }),
      }),
    );

  const snooze = () =>
    act('Snooze', () =>
      api(`/tasks/${task.id}/snooze`, { method: 'POST', body: JSON.stringify({ days: 3 }) }),
    );

  const restore = () => act('Restored', () => api(`/tasks/${task.id}/restore`, { method: 'POST' }));

  /*
   * Scheduled is the last exit before a real email leaves, so Skip and Snooze
   * stay live there — and only there. A `skipped` row offers Restore as its
   * primary instead; `blocked`, `snoozed`, `stopped` and `expired` are the
   * read-only record and register nothing, which is why the footer goes quiet
   * on them rather than offering keys that would refuse.
   */
  useRegisterQueueActions(
    busy !== null
      ? {}
      : region === 'scheduled'
        ? {
            snooze: { label: 'snooze 3d', run: () => void snooze() },
            skip: { label: 'skip — cancel send', run: () => void skip() },
          }
        : task.status === 'skipped'
          ? { primary: { label: 'put back in queue', run: () => void restore() } }
          : {},
  );

  const theirTime = absoluteInZone(task.targetAt, task.recipient.timeZone);

  const statusLine =
    region === 'scheduled'
      ? task.targetAt
        ? isPast(task.targetAt)
          ? /*
             * The slot is a TARGET, not the send itself: a separate scheduler
             * pass (every 20 minutes) releases due sends to the mail queue.
             * Rendered as "sends 5m ago" this read like a send that had
             * already happened — to a rep with an empty Sent folder, that is
             * indistinguishable from a bug.
             */
            `Approved — its ${absolute(task.targetAt)} slot has arrived. The scheduler releases due sends on its next pass (it runs every 20 minutes), then it leaves ${task.mailbox?.email ? `from ${task.mailbox.email}` : 'the mailbox'} within moments.`
          : // Their clock, then the rep's — the slot was picked to land inside
            // the recipient's working day, so theirs is the one that explains
            // it. Collapses to one time when the two zones agree.
            `Approved — sends ${relative(task.targetAt)}${
              theirTime && theirTime !== absolute(task.targetAt)
                ? ` (${theirTime} their time · ${absolute(task.targetAt)} your time)`
                : ` (${absolute(task.targetAt)})`
            }.`
        : 'Approved — waiting for the scheduler to assign a slot.'
      : region === 'done'
        ? task.status === 'sent'
          ? `Sent ${absolute(task.sentAt ?? task.updatedAt)}.`
          : `Completed ${absolute(task.updatedAt)}.`
        : `${STATE_LABEL[task.status] ?? task.status}${task.blockedReason ? ` — ${task.blockedReason}` : ''}${
            task.unlocksAt ? ` Unlocks ${relative(task.unlocksAt)}.` : ''
          }`;

  return (
    <div className="detail">
      <DetailHead task={task} statusLine={statusLine} />

      <div className="detail-grid">
        <div className="detail-main">
          {/*
            The RENDERED message, not the stored draft.

            This showed `task.bodyHtml` raw, so an approved email waiting for
            its slot displayed "{{sender_name}}" where the signature belongs —
            merge fields resolve at send time, and this view never merged them.
            A rep checking what they just approved saw the single most
            recognisable automation failure there is, in their own outbox.
          */}
          {task.bodyHtml ? (
            <PreviewPane taskId={task.id} refreshKey={`${task.subject}|${task.bodyHtml}`} />
          ) : task.instruction ? (
            <p className="why">{task.instruction}</p>
          ) : (
            <p className="why">No content was generated for this step.</p>
          )}

          {/*
            Skip is the one terminal state a human reaches by accident — one
            click, no confirmation — and it had no way back, so a misclick
            meant re-enrolling the contact. Offered only for `skipped`: a
            `stopped` step was stopped by a consent or suppression decision,
            and undoing that here would step around the rule.
          */}
          {task.status === 'skipped' && (
            <div className="draft-actions">
              <button
                type="button"
                className="primary"
                disabled={busy !== null}
                {...tip(
                  'Puts this step back in the queue awaiting approval. Refused if it already reached the provider, so it cannot cause a second delivery. Shortcut: Space.',
                )}
                onClick={() => void restore()}
              >
                {busy === 'Restored' ? 'Putting back…' : 'Put back in the queue'}
              </button>
            </div>
          )}

          {region === 'scheduled' && (
            <>
              <div className="draft-actions">
                <button
                  type="button"
                  disabled={busy !== null}
                  {...tip(
                    'Cancels the queued send and abandons this step. Later steps still run. This is the last moment it can be pulled back. Shortcut: X.',
                  )}
                  onClick={() => void skip()}
                >
                  {busy === 'Skip' ? 'Skipping…' : 'Skip — cancel the send'}
                </button>
                <button
                  type="button"
                  disabled={busy !== null}
                  {...tip(
                    'Pushes the scheduled send back three days and parks this in Not sending until then. Shortcut: S.',
                  )}
                  onClick={() => void snooze()}
                >
                  Snooze 3d
                </button>
              </div>
              <p className="fine">
                The scheduler releases this at its slot inside the recipient&rsquo;s business
                hours (§5.2). Until then, Skip cancels it entirely.
              </p>
            </>
          )}

          {/*
            The region is permanent and only its contents change. Mounting the
            note and its live region in the same commit gives a screen reader
            nothing to compare against, so the outcome of Restore, Skip and
            Snooze — the failures included — went unannounced.
          */}
          <div role="status" aria-live="polite">
            {message && (
              <p className={message.kind === 'error' ? 'error-note' : 'ok-note'}>{message.text}</p>
            )}
          </div>
        </div>

        <TaskMeta task={task} />
      </div>
    </div>
  );
}

/**
 * The steers reps reach for, as one click each — R10.
 *
 * Free text still works and always did; these exist because a grey placeholder
 * is a suggestion most people leave untaken, and an empty steer regenerates the
 * same email a second time. Phrased as instructions rather than labels so they
 * read correctly when two are combined.
 */
const STEER_PRESETS = [
  'make it shorter',
  'change the opener',
  'warmer, less formal',
  'lead with the deadline',
];

const REGENERATE_CONFIRM =
  'Writing a new draft replaces everything in this one, including your edits. Continue?';

/**
 * Rewrite just what is selected — R10.
 *
 * Regenerate is all-or-nothing, and its own tooltip admits it throws the rep's
 * edits away. That makes the small fix — the opener is wrong, the second
 * paragraph is twice as long as it needs to be — cost either a hand rewrite or
 * every other change on the draft. Reps do the hand rewrite, which is the
 * "review, not rewrite" contract of §6.1 quietly failing.
 *
 * The endpoint is handed the selected fragment and nothing else, so the rest of
 * the body cannot come back changed. Preservation is structural here, not a
 * request the model is trusted to honour.
 */
function RewriteSelection({
  taskId,
  selection,
  disabled,
  onReplaced,
}: {
  taskId: number;
  selection: { start: number; end: number; text: string } | null;
  disabled: boolean;
  onReplaced: (replacement: string) => void;
}) {
  const [steer, setSteer] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!selection || !selection.text.trim()) {
    return (
      <p className="fine">
        Select a sentence or paragraph above to rewrite just that part, leaving the rest of the
        draft alone.
      </p>
    );
  }

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api<{ replacement: string }>(`/tasks/${taskId}/rewrite`, {
        method: 'POST',
        body: JSON.stringify({ selection: selection.text, steer: steer.trim() }),
      });
      onReplaced(result.replacement);
      setSteer('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rewrite-box">
      <p className="fine">
        Rewriting <strong>{selection.text.trim().slice(0, 80)}</strong>
        {selection.text.trim().length > 80 ? '…' : ''}
      </p>
      <div className="steer-presets">
        {STEER_PRESETS.map((preset) => (
          <button
            key={preset}
            type="button"
            className="chip"
            disabled={busy || disabled}
            {...tip('Puts this into the box beside it. Nothing changes until you press Rewrite.')}
            onClick={() => setSteer((v) => (v.trim() ? `${v.trim()} · ${preset}` : preset))}
          >
            {preset}
          </button>
        ))}
      </div>
      <div className="rewrite-row">
        <input
          type="text"
          value={steer}
          placeholder="what to change about this part"
          disabled={busy || disabled}
          onChange={(e) => setSteer(e.target.value)}
        />
        <button
          type="button"
          disabled={busy || disabled || !steer.trim()}
          {...tip(
            'Rewrites only the selected text and pastes the result back in its place. The rest of the draft, and every other edit you have made, is untouched.',
          )}
          onClick={() => void run()}
        >
          {busy ? 'Rewriting…' : 'Rewrite this part'}
        </button>
      </div>
      {error && <p className="error-note">{error}</p>}
    </div>
  );
}

/**
 * How much room this mailbox has left, next to the button that spends it — R11.
 *
 * The capacity chip has always said "12 of 34 used today", but it lives in the
 * toolbar and describes every mailbox at once. The rep approving THIS draft is
 * spending one specific mailbox's allowance, and the moment that matters is the
 * moment they press Approve — a send that has to wait until tomorrow because
 * the cap ran out is not refused, it is just quietly late, and nothing said so.
 *
 * Deliberately not a gate. Gate 4 decides what may actually go, at release
 * time, with the same arithmetic this reads; putting a second decision here
 * would give the two a chance to disagree. This only tells the rep what they
 * are about to do.
 *
 * Silent when there is nothing worth saying. A mailbox with plenty of room, or
 * one whose capacity has not loaded, renders nothing at all — a permanent
 * "capacity fine" line beside Approve would be noise on every draft, and noise
 * beside a button is how a real warning stops being read.
 */
function MailboxHeadroom({
  capacity,
  mailboxId,
}: {
  capacity: Capacity | null;
  mailboxId: number | null;
}) {
  if (!capacity || mailboxId === null) return null;
  const mailbox = capacity.mailboxes.find((m) => m.id === mailboxId);
  if (!mailbox) return null;

  const throttled = mailbox.throttlePct < 100;
  // A quarter of the cap, or three sends, whichever is larger — a warm-up
  // mailbox capped at 20 should not spend its last quarter in silence, and a
  // mailbox at 200 does not need warning at 50.
  const nearLimit = mailbox.remaining <= Math.max(3, Math.round(mailbox.cap * 0.25));
  if (!throttled && !nearLimit && mailbox.bouncedWeek === 0) return null;

  const parts = [
    mailbox.remaining === 0
      ? `${mailbox.email} has no room left today — this would wait for tomorrow's allowance.`
      : nearLimit
        ? `${mailbox.remaining} of ${mailbox.cap} sends left on ${mailbox.email} today.`
        : null,
    throttled
      ? `It is throttled to ${mailbox.throttlePct}% of its usual cap${mailbox.throttleUntil ? ` until ${absolute(mailbox.throttleUntil)}` : ''}.`
      : null,
    mailbox.bouncedWeek > 0
      ? `${mailbox.bouncedWeek} message${mailbox.bouncedWeek === 1 ? '' : 's'} bounced from it in the last week.`
      : null,
  ].filter(Boolean);

  return (
    <p className="advisory-note" style={{ flexBasis: '100%' }}>
      {parts.join(' ')} Approving is still fine — the scheduler holds anything over the cap rather
      than sending it.
    </p>
  );
}

function Draft({
  task,
  role,
  onChanged,
  onApproved,
  onDirty,
  onOptimisticApprove,
  onRollbackApprove,
  capacity,
}: {
  task: Task;
  role: Role;
  onChanged: () => Promise<void>;
  onApproved: () => void;
  onDirty: (dirty: boolean) => void;
  /** Move the card on the server's answer, before the board refetches (R7). */
  onOptimisticApprove: (id: number, targetAt: string | null) => void;
  /** Put it back when the approve was refused. A no-op if it never moved. */
  onRollbackApprove: (id: number) => void;
  capacity: Capacity | null;
}) {
  const [subject, setSubject] = useState(task.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState(task.bodyHtml ?? '');
  /**
   * C.2 · M-SCH-1 — `tasks.deadline` had no producer at all before this: the
   * starvation notice's own remedy, "Set a deadline to promote it," named an
   * action nobody could take. The date input's own change event already
   * fires once per completed pick rather than per keystroke, so this PATCHes
   * directly — the debounced autosave above exists for free text, not for a
   * control that commits on selection.
   */
  const [deadline, setDeadline] = useState(task.deadline ? task.deadline.slice(0, 10) : '');
  const [deadlineBusy, setDeadlineBusy] = useState(false);
  const [guards, setGuards] = useState<Guard[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [steering, setSteering] = useState(false);
  const [steer, setSteer] = useState('');
  /**
   * Approve came back "guard failed". For a role holding the override
   * capability the button re-labels to "Send anyway" and the SECOND press
   * carries the override — a deliberate two-step, so nobody sends past a
   * guard they have not read. The server re-checks the capability; this
   * flag only decides what the button offers.
   */
  const [guardRefused, setGuardRefused] = useState(false);
  const mayOverride = can(role, 'task.approve.override');
  /** Preview first: what the prospect receives is the thing being approved. */
  const [view, setView] = useState<'preview' | 'edit'>('preview');

  /**
   * Autosave — there is no Save button. The draft saves itself ~a second
   * after typing stops, and anything that needs the saved copy (Approve, the
   * Preview tab) flushes first.
   *
   * Three copies of the content exist and each has a job:
   *   - component state (`subject`/`bodyHtml`) renders the fields,
   *   - `latestRef` is what the debounced save will write (a ref, so the
   *     timer callback never captures a stale keystroke),
   *   - `savedRef` is what the server currently holds — the thing "unsaved"
   *     is measured against, and the echo test that stops a poll from
   *     clobbering typing.
   */
  const savedRef = useRef({ subject: task.subject ?? '', body: task.bodyHtml ?? '' });
  const latestRef = useRef({ subject: task.subject ?? '', body: task.bodyHtml ?? '' });
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const taskIdRef = useRef<number | null>(null);
  /** When the last autosave landed — the poll-race grace period keys on it. */
  const lastSaveAt = useRef(0);
  const [saveState, setSaveState] = useState<'idle' | 'pending' | 'saving' | 'saved' | 'error'>(
    'idle',
  );
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * Bumped whenever the SERVER's copy of the draft changes — a landed save, a
   * regeneration, an adopted external edit — because that is exactly when the
   * preview (which renders the server's copy) must re-fetch. Keying it on
   * saves alone left a regenerated draft previewing its predecessor until the
   * rep happened to toggle Edit and back, remounting the pane by accident.
   */
  const [savedVersion, setSavedVersion] = useState(0);

  const cancelPendingSave = useCallback(() => {
    if (saveTimer.current !== null) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
  }, []);

  /**
   * Write whatever is unsaved, now. Loops because a save takes time and the
   * rep may still be typing — each pass writes the newest content, until the
   * server copy and the fields agree. Throws on failure so Approve can refuse
   * to proceed on top of an unsaved edit.
   */
  const flushSave = useCallback(async (): Promise<void> => {
    cancelPendingSave();
    for (;;) {
      const target = { ...latestRef.current };
      if (target.subject === savedRef.current.subject && target.body === savedRef.current.body) {
        break;
      }
      setSaveState('saving');
      try {
        await api(`/tasks/${taskIdRef.current}`, {
          method: 'PATCH',
          body: JSON.stringify({ subject: target.subject, bodyHtml: target.body }),
        });
      } catch (e) {
        setSaveState('error');
        setSaveError(e instanceof ApiError ? e.message : String(e));
        throw e;
      }
      savedRef.current = target;
      lastSaveAt.current = Date.now();
      setSavedVersion((v) => v + 1);
    }
    setSaveState((s) => (s === 'saving' || s === 'pending' ? 'saved' : s));
    setSaveError(null);
  }, [cancelPendingSave]);

  /** Every keystroke funnels through here — field update + debounced save. */
  const edit = useCallback(
    (field: 'subject' | 'body', value: string) => {
      if (field === 'subject') setSubject(value);
      else setBodyHtml(value);
      latestRef.current =
        field === 'subject'
          ? { ...latestRef.current, subject: value }
          : { ...latestRef.current, body: value };
      cancelPendingSave();
      setSaveState('pending');
      saveTimer.current = setTimeout(() => {
        // Failure is already reflected in saveState; typing again retries.
        void flushSave().catch(() => {});
      }, 900);
    },
    [cancelPendingSave, flushSave],
  );

  /**
   * The token picker's raw material: this recipient's live values, from the
   * same endpoint the preview renders with — so the value shown next to
   * {{company}} is the value the merge will actually use. Fetched once per
   * task, lazily, the first time Edit is opened.
   */
  const [tokenValues, setTokenValues] = useState<Record<string, string | null> | null>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const lastFocused = useRef<'subject' | 'body'>('body');

  useEffect(() => {
    if (view !== 'edit' || tokenValues !== null) return;
    let cancelled = false;
    api<{ tokens: { name: string; value: string | null }[] }>(`/tasks/${task.id}/preview`)
      .then((d) => {
        if (!cancelled) {
          setTokenValues(Object.fromEntries(d.tokens.map((t) => [t.name, t.value])));
        }
      })
      .catch(() => {
        // No values (no draft yet, or a fetch hiccup): the picker still works,
        // it just cannot show what each token resolves to.
        if (!cancelled) setTokenValues({});
      });
    return () => {
      cancelled = true;
    };
  }, [view, task.id, tokenValues]);

  /**
   * Insert `{{token}}` at the caret of whichever field was last focused.
   * A token that is EMPTY for this recipient is inserted as `{{token|}}` with
   * the caret parked inside the fallback slot — the guard will refuse an
   * empty token without one, so the picker steers straight to the fix.
   */
  const insertToken = (name: string) => {
    /**
     * A minted token never gets a fallback slot.
     *
     * For an ordinary field a fallback is the author saying what to do when it
     * is empty. For a link that is created at send time it is the opposite:
     * `{{booking_link|}}` renders as an empty string, so the email promises a
     * meeting and links to nothing — silently. Absent is a refusal to send;
     * empty is not.
     */
    const minted = MERGE_TOKENS.find((t) => t.name === name)?.minted === true;
    const empty = !minted && !tokenValues?.[name];
    const snippet = empty ? `{{${name}|}}` : `{{${name}}}`;
    const caretInside = empty ? snippet.length - 2 : snippet.length;

    const field = lastFocused.current;
    const el = field === 'subject' ? subjectRef.current : bodyRef.current;
    const value = field === 'subject' ? subject : bodyHtml;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = value.slice(0, start) + snippet + value.slice(end);

    // Through edit(), so an inserted token autosaves like a keystroke would.
    edit(field, next);

    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + caretInside, start + caretInside);
    });
  };

  /**
   * The Insert-video flow. Picking a video first ATTACHES it to this task —
   * the API mints the recipient's personal link right then (idempotent, so
   * preview and send reuse the same one) — and only on success inserts the
   * {{video_embed}} placeholder into the body, at the caret, on its own line.
   */
  const [attachedVideo, setAttachedVideo] = useState<{ title: string; url: string } | null>(null);
  const [videoBusy, setVideoBusy] = useState(false);

  const insertVideoEmbed = () => {
    const el = bodyRef.current;
    const start = el?.selectionStart ?? bodyHtml.length;
    const end = el?.selectionEnd ?? bodyHtml.length;
    const snippet = '\n{{video_embed}}\n';
    edit('body', bodyHtml.slice(0, start) + snippet + bodyHtml.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + snippet.length, start + snippet.length);
    });
  };

  const attachVideo = async (v: { slug: string; title: string }) => {
    setVideoBusy(true);
    setMessage(null);
    try {
      const d = await api<{ ok: boolean; url: string; thumbUrl: string | null }>(
        `/tasks/${task.id}/video`,
        { method: 'POST', body: JSON.stringify({ slug: v.slug, title: v.title }) },
      );
      setAttachedVideo({ title: v.title, url: d.url });
      // Don't double-place it if the draft already carries a video token.
      if (!/\{\{\s*video_(embed|link)\b/i.test(bodyHtml)) insertVideoEmbed();
      setMessage({ kind: 'ok', text: `Video link created for this contact: ${d.url}` });
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setVideoBusy(false);
    }
  };

  const changeDeadline = async (value: string) => {
    setDeadline(value);
    setDeadlineBusy(true);
    setMessage(null);
    try {
      await api(`/tasks/${task.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ deadline: value === '' ? null : new Date(value).toISOString() }),
      });
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setDeadlineBusy(false);
    }
  };

  const removeVideo = async () => {
    setVideoBusy(true);
    try {
      await api(`/tasks/${task.id}/video`, { method: 'DELETE' });
      setAttachedVideo(null);
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setVideoBusy(false);
    }
  };

  /**
   * Fetched separately from the draft so a regeneration can refresh the panel
   * the moment the new verdicts exist, rather than waiting for the next poll —
   * a stale "1 guard failed" under a draft that just fixed it is exactly the
   * confusion this panel is meant to remove.
   */
  const loadGuards = useCallback(async () => {
    try {
      const d = await api<{ guards: Guard[] }>(`/tasks/${task.id}/guards`);
      setGuards(d.guards);
      // Fresh verdicts describe the text as it is now.
      setGuardsStale(false);
    } catch {
      setGuards([]);
    }
  }, [task.id]);

  useEffect(() => {
    const incoming = { subject: task.subject ?? '', body: task.bodyHtml ?? '' };

    // A DIFFERENT task selected. If the old one still has an unflushed edit,
    // write it on the way out — fire-and-forget, to the OLD task id.
    if (taskIdRef.current !== task.id) {
      const oldId = taskIdRef.current;
      const stale = latestRef.current;
      if (
        oldId !== null &&
        (stale.subject !== savedRef.current.subject || stale.body !== savedRef.current.body)
      ) {
        void api(`/tasks/${oldId}`, {
          method: 'PATCH',
          body: JSON.stringify({ subject: stale.subject, bodyHtml: stale.body }),
        }).catch(() => {});
      }
      taskIdRef.current = task.id;
      cancelPendingSave();
      savedRef.current = incoming;
      latestRef.current = incoming;
      setSubject(incoming.subject);
      setBodyHtml(incoming.body);
      setSaveState('idle');
      setSaveError(null);
      setMessage(null);
      setGuards(null);
      setSteering(false);
      setSteer('');
      setView('preview');
      setTokenValues(null);
      void loadGuards();
      return;
    }

    /**
     * Same task, new content from the poll. An ECHO — the server returning
     * exactly what we saved — changes nothing, which is what lets the poll
     * run while someone is mid-sentence. Anything else (a regenerate, another
     * session) is adopted, and any pending stale save is cancelled so it
     * cannot land on top of the newer copy.
     *
     * Two shields before adopting, both against the same race — a poll
     * response that left the server BEFORE our latest save landed carries the
     * older content and must not overwrite it:
     *   - never adopt over unsaved keystrokes, and
     *   - never adopt inside a short grace after a local save. A genuine
     *     external change survives the grace and is adopted by the next poll.
     */
    const echo =
      incoming.subject === savedRef.current.subject && incoming.body === savedRef.current.body;
    const typing =
      latestRef.current.subject !== savedRef.current.subject ||
      latestRef.current.body !== savedRef.current.body;
    if (!echo && !typing && Date.now() - lastSaveAt.current > 15_000) {
      cancelPendingSave();
      savedRef.current = incoming;
      latestRef.current = incoming;
      setSubject(incoming.subject);
      setBodyHtml(incoming.body);
      setSaveState('idle');
      // Different content, different verdicts — disarm any pending override.
      setGuardRefused(false);
      // Adopted content is a server-side change like any other: re-fetch the
      // preview, or it keeps rendering the copy this poll just replaced.
      setSavedVersion((v) => v + 1);
      void loadGuards();
    }
  }, [task.id, task.subject, task.bodyHtml, loadGuards, cancelPendingSave]);

  const unsaved =
    subject !== savedRef.current.subject ||
    bodyHtml !== savedRef.current.body ||
    saveState === 'saving' ||
    saveState === 'pending';

  /**
   * Has a person touched this copy? — R10.
   *
   * Compared against what the WRITER produced, not against the last save, so
   * it stays true after an edit has been autosaved. That is the whole point:
   * the confirm on Regenerate has to fire for work already written to the
   * server, which is most of it.
   */
  const humanEdited = subject !== (task.subject ?? '') || bodyHtml !== (task.bodyHtml ?? '');

  /**
   * The body text the rep has highlighted, for the targeted rewrite.
   *
   * Tracked here rather than read at click time because clicking the rewrite
   * button moves focus out of the textarea, and on some browsers that clears
   * the selection before any handler sees it.
   */
  const [selection, setSelection] = useState<{ start: number; end: number; text: string } | null>(
    null,
  );

  /**
   * True once a targeted rewrite has changed the body since the guards last
   * ran — R10. What it suppresses is the "All guards passed" line, because that
   * line would otherwise be vouching for model output no guard has read.
   */
  const [guardsStale, setGuardsStale] = useState(false);

  const rememberSelection = () => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: start, selectionEnd: end } = el;
    setSelection(start === end ? null : { start, end, text: el.value.slice(start, end) });
  };

  /**
   * Splice the rewritten fragment back in, exactly where the old one was.
   *
   * Through `edit()` so it autosaves like a keystroke — which is also what
   * makes it behave like one everywhere else: the draft becomes human-edited,
   * the poll pauses, and the guards are not re-run (they never are for a
   * manual edit; Regenerate remains the way to get a fresh pass).
   */
  const replaceSelection = (replacement: string) => {
    if (!selection) return;

    /*
     * The offsets are only meaningful against the body they were taken from.
     * A regenerate, or the poll adopting a newer draft, can replace the whole
     * text while the rewrite request is in flight — and splicing at the old
     * offsets would then cut the new draft at an arbitrary point and paste a
     * paragraph answering a question about text that is no longer there.
     * Cheaper to check than to reason about: if the range no longer holds what
     * was sent, drop the result rather than corrupt the draft.
     */
    if (bodyHtml.slice(selection.start, selection.end) !== selection.text) {
      setSelection(null);
      setMessage({
        kind: 'error',
        text: 'The draft changed while that was being rewritten, so nothing was replaced. Select the text again.',
      });
      return;
    }

    const next = bodyHtml.slice(0, selection.start) + replacement + bodyHtml.slice(selection.end);
    edit('body', next);
    setSelection(null);
    setGuardsStale(true);
    requestAnimationFrame(() => {
      const el = bodyRef.current;
      el?.focus();
      const caret = selection.start + replacement.length;
      el?.setSelectionRange(caret, caret);
    });
  };

  // The poll must not clobber an edit in progress — the parent pauses it while
  // any draft has unsaved keystrokes, and this is how it knows. The window is
  // short now (autosave), but it exists.
  useEffect(() => {
    onDirty(unsaved);
    return () => onDirty(false);
  }, [unsaved, onDirty]);

  // Unmounting with a pending edit: write it, fire-and-forget. Losing the
  // last keystroke because someone closed the panel quickly is the exact bug
  // autosave exists to remove.
  useEffect(
    () => () => {
      cancelPendingSave();
      const latest = latestRef.current;
      const saved = savedRef.current;
      if (
        taskIdRef.current !== null &&
        (latest.subject !== saved.subject || latest.body !== saved.body)
      ) {
        void api(`/tasks/${taskIdRef.current}`, {
          method: 'PATCH',
          body: JSON.stringify({ subject: latest.subject, bodyHtml: latest.body }),
        }).catch(() => {});
      }
    },
    [cancelPendingSave],
  );

  /** Resolves true only when the action landed — auto-advance keys off that. */
  const act = async (label: string, fn: () => Promise<unknown>): Promise<boolean> => {
    setBusy(label);
    setMessage(null);
    try {
      await fn();
      await onChanged();
      setMessage({ kind: 'ok', text: `${label} done.` });
      return true;
    } catch (e) {
      setMessage({
        kind: 'error',
        text: e instanceof ApiError ? e.message : String(e),
      });
      return false;
    } finally {
      setBusy(null);
    }
  };

  /*
   * One definition per action, shared by the button and the shortcut.
   *
   * The keyboard must not become a second, subtly different path to the same
   * effect — a Space that skipped the `flushSave()` an Approve click performs
   * would send the draft as it was a second ago.
   */
  /**
   * Not through act(): this needs the refusal's CODE, which act() flattens
   * into a message. A `guard_failed` refusal arms the second attempt — the
   * button becomes "Send anyway" for roles that may override — while every
   * other refusal stays a plain error.
   */
  const approve = async () => {
    const sendAnyway = guardRefused && mayOverride;
    const label = sendAnyway ? 'Send anyway' : 'Approve';
    setBusy(label);
    setMessage(null);
    try {
      // The edit still in its debounce window is part of what is being
      // approved — write it first, and refuse to approve on a failed save.
      await flushSave();
      const result = await api<{ id: number; status: string; targetAt: string | null }>(
        `/tasks/${task.id}/approve`,
        {
          method: 'POST',
          body: JSON.stringify(sendAnyway ? { sendAnyway: true } : {}),
        },
      );
      setGuardRefused(false);

      /*
       * Move the card NOW, on the server's own answer — R7.
       *
       * The approve has committed by this line; the only thing still
       * outstanding is a refetch of the whole board, and waiting for it left
       * the row sitting in the queue looking untouched for as long as that
       * took. The response already carries the assigned send slot, so this is
       * not a guess about what happened, it is the result rendered early.
       *
       * `onChanged()` still runs, unawaited: everything else on the board
       * (counts, the next card's guards, another rep's work) still needs to
       * catch up — it just no longer gates the one card the rep is looking at.
       */
      onOptimisticApprove(result.id, result.targetAt);
      void onChanged();

      setMessage({
        kind: 'ok',
        text: sendAnyway ? 'Approved over the failing guards — the override is recorded.' : 'Approve done.',
      });
      // Only on success: advancing off a refused approve would hide the refusal.
      onApproved();
    } catch (e) {
      // The card never moved, so there is nothing to roll back unless the
      // optimistic patch already landed — `onRollbackApprove` is a no-op when
      // it did not, which keeps this branch free of a second condition to get
      // wrong.
      onRollbackApprove(task.id);
      if (e instanceof ApiError && e.code === 'guard_failed') setGuardRefused(true);
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };

  const showPreview = () => {
    // Flush any edit still in its debounce window; the preview re-fetches when
    // the save lands (savedVersion bumps).
    void flushSave().catch(() => {});
    setView('preview');
  };
  const showEdit = () => setView('edit');
  const toggleEdit = () => (view === 'edit' ? showPreview() : showEdit());
  const toggleSteer = () => setSteering((v) => !v);

  const snooze = () =>
    act('Snooze', () =>
      api(`/tasks/${task.id}/snooze`, { method: 'POST', body: JSON.stringify({ days: 3 }) }),
    );

  const skip = () =>
    act('Skip', () =>
      api(`/tasks/${task.id}/skip`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Skipped from the queue.' }),
      }),
    );

  /**
   * Stop the whole cadence — §15's listed action, finally built.
   *
   * Confirmed, because it is the only control here that ends more than the
   * thing on screen, and it cannot be undone from this pane. The confirm names
   * the sequence and states the limit of what stops, since the risk with this
   * button is someone pressing it believing it also blocks the contact.
   */
  const stopSequence = () => {
    const what = task.sequenceName ? `the “${task.sequenceName}” sequence` : 'this sequence';
    if (
      !confirm(
        `Stop ${what} for ${task.recipient.name ?? task.recipient.email ?? 'this contact'}?\n\n` +
          'Every remaining step ends and any queued send is cancelled. Their consent is not ' +
          'changed and they are not added to the blocklist — another sequence could still ' +
          'reach them.',
      )
    ) {
      return;
    }
    void act('Stop sequence', () =>
      api(`/tasks/${task.id}/stop-sequence`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Stopped by the rep from the queue.' }),
      }),
    );
  };

  /**
   * Report a draft as wrong — §15's "Flag content issue".
   *
   * Deliberately separate from every other action here: flagging changes
   * nothing about the task. A rep may flag this draft and then approve it,
   * because "this is the best of a bad set and I am sending it anyway" is
   * exactly the report the writer's prompt most needs and the one a control
   * coupled to Skip would never collect.
   */
  const flagContent = () => {
    const note = prompt(
      'What is wrong with this draft?\n\nThis is read to improve the writer. It does not skip, stop or change the draft — you can still edit, regenerate or approve it.',
    );
    if (note === null || !note.trim()) return;
    void act('Flag', () =>
      api(`/tasks/${task.id}/flag`, {
        method: 'POST',
        body: JSON.stringify({ kind: 'rep_flag', note }),
      }),
    );
  };

  const failing = guards?.filter((g) => !g.passed) ?? [];

  /*
   * Publish the shortcuts this pane is actually showing buttons for.
   *
   * Gated on the same condition the action bar is: a manual step or an
   * ungenerated draft renders no buttons, and the footer must not offer keys for
   * buttons that are not on screen. Guard failures are deliberately NOT excluded
   * — the Approve button stays clickable and the server refuses, so the keyboard
   * mirrors the mouse rather than inventing a second, stricter rule.
   */
  const hasDraftActions = task.stepType === 'email' && Boolean(task.bodyHtml);
  /**
   * An integration supplied this email's text (plans/external-send-api.md A4):
   * regenerating would replace its words with a model's, which the server
   * refuses — so the control and its shortcut are not offered at all.
   */
  const canRegenerate = task.source?.mode !== 'html';
  useRegisterQueueActions(
    hasDraftActions && busy === null
      ? {
          primary: {
            label: guardRefused && mayOverride ? 'send anyway' : 'approve',
            run: () => void approve(),
          },
          edit: { label: view === 'edit' ? 'preview' : 'edit', run: toggleEdit },
          ...(canRegenerate
            ? { regenerate: { label: steering ? 'cancel regen' : 'regenerate', run: toggleSteer } }
            : {}),
          snooze: { label: 'snooze 3d', run: () => void snooze() },
          skip: { label: 'skip', run: () => void skip() },
        }
      : {},
  );

  return (
    <div className="detail">
      <DetailHead task={task} />

      <div className="detail-grid">
        <div className="detail-main">
          {/* Manual steps are the rep's work, not ActOS's. They have no draft and
              never will, so offering an approve button would be a lie. */}
          {task.stepType !== 'email' ? (
            <ManualStep task={task} onChanged={onChanged} />
          ) : !task.bodyHtml ? (
            /* A blocked task without a draft is blocked FIRST — saying "the
               draft is coming" about a paused contact promises the opposite
               of the truth. */
            task.blockedReason ? (
              <p className="why">
                Not drafting: {task.blockedReason}
                {task.unlocksAt ? ` Unlocks ${absolute(task.unlocksAt)}.` : ''}
              </p>
            ) : (
              <p className="why">
                No draft yet. Content is generated inside the lookahead window, shortly before this
                step becomes visible ({absolute(task.visibleAt)}).
              </p>
            )
          ) : (
            <>
              {/* §6.1 threading, stated. "Why did this arrive as a new email"
                  should be answerable before it is sent, not after. */}
              {task.threadMode && (
                <p className="fine thread-note">
                  {task.threadMode === 'reply'
                    ? 'Sends as a reply in the existing conversation, subject prefixed Re:.'
                    : 'Sends as a new thread with its own subject.'}
                </p>
              )}

              <nav className="subnav">
                <button
                  type="button"
                  className={view === 'preview' ? 'active' : ''}
                  {...tip(
                    'Shows the email exactly as the prospect receives it — merge fields resolved, postal address and any disclosure appended. Rendered by the same code that sends.',
                  )}
                  onClick={showPreview}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className={view === 'edit' ? 'active' : ''}
                  {...tip(
                    'Edit the raw draft — it saves itself as you type. Merge fields like {{first_name}} resolve at send time; the preview shows the result. Shortcut: E.',
                  )}
                  onClick={showEdit}
                >
                  Edit
                </button>
              </nav>

              {view === 'preview' && (
                <>
                  {(saveState === 'saving' || saveState === 'pending') && (
                    <p className="fine">Saving your edit — the preview refreshes when it lands.</p>
                  )}
                  <PreviewPane taskId={task.id} refreshKey={`v${savedVersion}`} />
                </>
              )}

              {view === 'edit' && (
                <>
                  <label
                    className="field"
                    {...tip('The line the recipient sees first. Saves itself as you type and marks the draft human-edited.')}
                  >
                    <span>Subject</span>
                    <input
                      ref={subjectRef}
                      value={subject}
                      onFocus={() => {
                        lastFocused.current = 'subject';
                      }}
                      onChange={(e) => edit('subject', e.target.value)}
                    />
                  </label>

                  <label
                    className="field"
                    {...tip(
                      'A dated send jumps to the front of the queue as its deadline nears, regardless of SDR/marketing class. Clear it to go back to ordinary priority.',
                    )}
                  >
                    <span>Due by</span>
                    <input
                      type="date"
                      value={deadline}
                      disabled={deadlineBusy}
                      onChange={(e) => void changeDeadline(e.target.value)}
                    />
                  </label>

                  <label
                    className="field"
                    {...tip('The message that will be sent. Saves itself as you type. Merge fields like {{first_name}}, {{company}} or {{first_name|there}} resolve from the contact and account at send time.')}
                  >
                    <span>Body</span>
                    <textarea
                      ref={bodyRef}
                      rows={14}
                      value={bodyHtml}
                      // Described by the format hint below rather than by the
                      // label's tip: a tip anchors to the element carrying
                      // `data-tip` — here the wrapping label — so it is never
                      // announced to someone who arrives in the box by keyboard.
                      aria-describedby="draft-body-format"
                      onFocus={() => {
                        lastFocused.current = 'body';
                      }}
                      onSelect={rememberSelection}
                      onKeyUp={rememberSelection}
                      onMouseUp={rememberSelection}
                      onChange={(e) => edit('body', e.target.value)}
                    />
                  </label>

                  <RewriteSelection
                    taskId={task.id}
                    selection={selection}
                    disabled={busy !== null}
                    onReplaced={replaceSelection}
                  />

                  {/* Nothing on this tab said the box was markup. A rep typing
                      plain paragraphs got one run-on block in the recipient's
                      inbox, and only found out by reading Preview. */}
                  <p className="fine" id="draft-body-format">
                    This box takes HTML, not plain text: wrap each paragraph in{' '}
                    <code>{'<p>…</p>'}</code>. A line break typed here is not a line break in the
                    email — Preview shows what actually arrives.
                  </p>

                  <p className="fine save-status" aria-live="polite">
                    {saveState === 'pending' || saveState === 'saving'
                      ? 'Saving…'
                      : saveState === 'saved'
                        ? 'All changes saved.'
                        : saveState === 'error'
                          ? `Autosave failed: ${saveError ?? 'unknown error'}. It retries as you type; Approve will also refuse until it lands.`
                          : 'Edits save automatically and mark the draft as human-edited.'}
                  </p>

                  <TokenPicker values={tokenValues} onInsert={insertToken} />
                  <VideoPicker
                    onPick={(v) => void attachVideo(v)}
                    busy={videoBusy}
                    attached={attachedVideo}
                    onRemove={() => void removeVideo()}
                  />
                </>
              )}

              {/* The guard's own words, verbatim — §6.1. A block with no stated
                  reason is a block the rep cannot clear. */}
              {failing.length > 0 && (
                <div className="guard-panel">
                  <p className="guard-title">
                    {failing.length} guard{failing.length === 1 ? '' : 's'} failed
                  </p>
                  {failing.map((guard, i) => (
                    <p key={i} className="guard-item">
                      <span className="guard-kind">{guard.guard_kind}</span>
                      <span className="guard-mech">{guard.mechanism}</span>
                      {guard.reason}
                    </p>
                  ))}
                  <p className="fine">
                    {mayOverride
                      ? 'Regenerate to re-run the guards — or press Approve and, on the refusal, Send anyway to approve over them. The override is recorded with the verdicts it overrode.'
                      : 'Regenerate to re-run the guards, or ask an admin to send it anyway. The guard exists because the failure it caught is one a recipient would notice.'}
                  </p>
                </div>
              )}

              {/*
                "All guards passed" must describe the text on screen, not the
                text the writer produced before a rewrite replaced part of it.

                A hand edit has always had this property and it was tolerable:
                the rep wrote those words and knows what is in them. A targeted
                rewrite (R10) does not — that paragraph is fresh model output no
                guard has read, and reporting it green would be the panel
                vouching for copy it has never seen. Guards do not re-run on an
                edit (§6.1 runs them at generation), so the honest thing is to
                stop claiming, not to claim something weaker.
              */}
              {guards && failing.length === 0 && !guardsStale && (
                <p className="guard-ok">All {guards.length} guards passed.</p>
              )}

              {guards && guardsStale && (
                <p className="advisory-note">
                  <strong>The guards have not seen this text.</strong> Part of this draft was
                  rewritten after they last ran, so their verdicts describe the earlier version.
                  Regenerate to get a fresh pass, or read the rewritten part yourself before
                  approving — approving is your judgement either way.
                </p>
              )}

              {/*
                An ADVISORY review reason — every guard passed, but something
                is worth a human's attention before this goes out (§6's
                coverage policy: a draft written with no matched persona).
                It used to be invisible here while the approve button
                returned "a content guard failed", which was both wrong and
                unactionable. Approving IS the review it asks for.
              */}
              {task.needsReview && failing.length === 0 && task.reviewReason && (
                <p className="advisory-note">
                  <strong>Worth a look before approving:</strong> {task.reviewReason}
                </p>
              )}

              {/* Pinned: the draft body, the guard verdicts and the advisory
                  notes all push this row below the fold, so the primary action
                  of the primary surface was off screen the moment an item was
                  opened. */}
              <div className="draft-actions is-pinned">
                {/*
                  The verdict lives INSIDE the pinned bar, above the buttons.
                  Below it, the row being sticky meant the note sat permanently
                  off the bottom of the pane: an Approve that came back REFUSED
                  was announced to a screen reader and shown to nobody.

                  Permanent region, changing contents — mounting the note and
                  its live region in the same commit gives a screen reader
                  nothing to compare against. Full-width so it takes its own
                  line in the wrapping flex row and the buttons keep theirs.
                */}
                <div role="status" aria-live="polite" style={{ flexBasis: '100%' }}>
                  {message && (
                    <p className={message.kind === 'error' ? 'error-note' : 'ok-note'}>
                      {message.text}
                    </p>
                  )}
                </div>

                <MailboxHeadroom capacity={capacity} mailboxId={task.mailbox?.id ?? null} />

                <button
                  type="button"
                  className="primary"
                  disabled={busy !== null}
                  {...tip(
                    guardRefused && mayOverride
                      ? 'Approves OVER the failing guards — an admin decision, recorded on the approval with the verdicts it overrode. The email still schedules inside working hours; Skip can still pull it back until the slot arrives. Shortcut: Space.'
                      : 'Authorises a real email to a real person, scheduled inside their working hours rather than sent now. Any edit still saving is written first — what you see is what is approved. Skip can still pull it back until the slot arrives. Shortcut: Space.',
                  )}
                  onClick={() => void approve()}
                >
                  {busy === 'Approve' || busy === 'Send anyway'
                    ? 'Approving…'
                    : guardRefused && mayOverride
                      ? 'Send anyway'
                      : 'Approve'}
                </button>

                {canRegenerate && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    {...tip('Throws this draft away — your edits included — and writes a new one from the same facts. Add a steer first to say what to change. Shortcut: R.')}
                    onClick={toggleSteer}
                  >
                    {steering ? 'Cancel regenerate' : 'Regenerate'}
                  </button>
                )}

                <button
                  type="button"
                  disabled={busy !== null}
                  {...tip('Parks this in Not sending for three days and pushes any scheduled send back with it. It returns to the queue then. Shortcut: S.')}
                  onClick={() => void snooze()}
                >
                  Snooze 3d
                </button>

                <button
                  type="button"
                  disabled={busy !== null}
                  {...tip(
                    'Abandons this step and cancels its queued send. Later steps still run. Cannot be undone from here. Shortcut: X.',
                  )}
                  onClick={() => void skip()}
                >
                  Skip
                </button>

                {/*
                  Only for a step that HAS a cadence to stop. A one-off's
                  "sequence" is itself, and Skip already ends it — offering a
                  button that can only refuse is the thing §15 calls a control
                  that lies about what it does.
                */}
                {task.sequenceName && (
                  <button
                    type="button"
                    disabled={busy !== null}
                    {...tip(
                      'Ends every remaining step of this sequence for this contact and cancels any queued send. Their consent is not changed and they are not blocked — another sequence could still reach them. Asks first, and cannot be undone from here.',
                    )}
                    onClick={stopSequence}
                  >
                    {busy === 'Stop sequence' ? 'Stopping…' : 'Stop sequence'}
                  </button>
                )}

                <button
                  type="button"
                  disabled={busy !== null}
                  {...tipAt(
                    'Records what is wrong with this draft, so the writer’s prompt can be fixed. Changes nothing about the task — you can flag it and still approve it.',
                    'end',
                  )}
                  onClick={flagContent}
                >
                  {busy === 'Flag' ? 'Recording…' : 'Flag content'}
                </button>
              </div>

              {/* The steer, optional by design: "write it again" is a valid
                  instruction on its own, and forcing a reason would make the
                  cheapest useful action the most tedious one. */}
              {steering && (
                <div className="steer-box">
                  <label className="field">
                    <span>What should change? (optional)</span>
                    <textarea
                      rows={2}
                      value={steer}
                      autoFocus
                      placeholder="shorter · lead with the security angle · drop the second paragraph"
                      onChange={(e) => setSteer(e.target.value)}
                    />
                  </label>
                  {/*
                    The four steers reps actually reach for, as one click each.
                    The box has always accepted them typed — the placeholder
                    even suggested two — but a free-text field with a suggestion
                    in grey is a field most people leave empty, and an empty
                    steer regenerates the same email a second time.
                  */}
                  <div className="steer-presets">
                    {STEER_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        className="chip"
                        disabled={busy !== null}
                        {...tip(
                          'Puts this into the steer box. Nothing is rewritten until you press the button below.',
                        )}
                        onClick={() => setSteer((v) => (v.trim() ? `${v.trim()} · ${preset}` : preset))}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <div className="draft-actions">
                    <button
                      type="button"
                      className="primary"
                      disabled={busy !== null}
                      {...tip('Writes a new draft now, following your steer as far as the known facts allow. It cannot invent detail to satisfy a steer. Every edit you have made to this draft is replaced.')}
                      onClick={() => {
                        /*
                          A full regenerate replaces everything, and the rep
                          may have spent five minutes on this copy. Ask — but
                          only when there is actually work to lose, so the
                          confirm stays meaningful instead of becoming the
                          dialog everyone clicks through.
                        */
                        if (humanEdited && !confirm(REGENERATE_CONFIRM)) return;
                        void act('Regenerate', async () => {
                          // A pending autosave of the OLD text must not land
                          // after the new draft arrives and overwrite it.
                          cancelPendingSave();
                          const result = await api<{ subject: string; bodyHtml: string; note: string }>(
                            `/tasks/${task.id}/regenerate`,
                            { method: 'POST', body: JSON.stringify({ steer: steer.trim() || undefined }) },
                          );
                          // Show the new copy AND its new verdicts immediately
                          // rather than waiting for the poll — the rep asked
                          // for this just now, and a stale guard panel under a
                          // fresh draft is worse than no panel. The new copy
                          // IS the saved copy, so the baseline adopts it.
                          savedRef.current = { subject: result.subject, body: result.bodyHtml };
                          latestRef.current = { subject: result.subject, body: result.bodyHtml };
                          setSubject(result.subject);
                          setBodyHtml(result.bodyHtml);
                          setSaveState('idle');
                          setSteering(false);
                          setSteer('');
                          // A fresh draft means fresh verdicts — a "Send
                          // anyway" armed against the old ones must not
                          // carry over to copy nobody has re-read.
                          setGuardRefused(false);
                          // The server's copy just changed without a save, so
                          // the preview must be told — it re-fetches on this.
                          setSavedVersion((v) => v + 1);
                          await loadGuards();
                          return result;
                        });
                      }}
                    >
                      {busy === 'Regenerate' ? 'Writing…' : 'Write a new draft'}
                    </button>
                  </div>
                </div>
              )}

              <p className="fine">
                Approving does not send immediately. It assigns a send slot inside the
                recipient&rsquo;s business-hours window, with jitter and a minimum gap from your
                other sends (§5.2). Edits save themselves; approving writes any edit still in
                flight first, so what you see is what is approved.
              </p>
            </>
          )}
        </div>

        <TaskMeta task={task} />
      </div>
    </div>
  );
}

const DISPOSITIONS: Record<string, { value: string; label: string }[]> = {
  call: [
    { value: 'connected', label: 'Connected' },
    { value: 'left_voicemail', label: 'Left voicemail' },
    { value: 'no_answer', label: 'No answer' },
    { value: 'wrong_number', label: 'Wrong number' },
    { value: 'meeting_booked', label: 'Booked a meeting' },
    { value: 'not_interested', label: 'Not interested' },
  ],
  linkedin: [
    { value: 'sent', label: 'Request sent' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'no_answer', label: 'No response' },
    { value: 'meeting_booked', label: 'Booked a meeting' },
    { value: 'not_interested', label: 'Not interested' },
  ],
  research: [{ value: 'completed', label: 'Done' }],
};

/**
 * A manual step — call, LinkedIn or research.
 *
 * ActOS cannot do these and must not pretend to. What it CAN do is say what the
 * step was for, record what happened, and act on the answer: booking a meeting
 * pauses the rest of the sequence, and so does "not interested".
 *
 * The disposition is required rather than optional. "Done" without an outcome
 * tells the next person nothing, and the two outcomes that change the sequence
 * are only knowable if someone says which one it was.
 */
function ManualStep({ task, onChanged }: { task: Task; onChanged: () => Promise<void> }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);

  const options = DISPOSITIONS[task.stepType] ?? DISPOSITIONS.research!;

  /**
   * Defer or abandon a manual step, through the same endpoints the draft pane
   * uses. Named apart from `complete` because they are the opposite of it: a
   * completion records what happened, these two record that nothing did.
   */
  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    setMessage(null);
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };

  const defer = () =>
    run('Snooze', () =>
      api(`/tasks/${task.id}/snooze`, { method: 'POST', body: JSON.stringify({ days: 3 }) }),
    );

  const abandon = () =>
    run('Skip', () =>
      api(`/tasks/${task.id}/skip`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'Skipped from the queue.' }),
      }),
    );

  const complete = async (disposition: string) => {
    setBusy(disposition);
    setMessage(null);
    try {
      const result = await api<{ crm?: { outcome: string; detail: string } | null }>(
        `/tasks/${task.id}/complete`,
        {
          method: 'POST',
          body: JSON.stringify({ disposition, note: note || undefined }),
        },
      );
      await onChanged();
      // The CRM heard about it (or refused) — say which, since a rep who
      // logs a call expects to find it on the HubSpot timeline.
      if (result.crm && result.crm.outcome !== 'skipped') {
        setMessage({
          kind: result.crm.outcome === 'written' ? 'ok' : 'error',
          text: result.crm.detail,
        });
      }
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="manual-step">
      <p className="variance-title">{task.stepType} step</p>

      {/*
        A research task raised by §17.5 belongs to no sequence, so it has no
        step instruction — what to do is in the reason it was raised. Falling
        back to "no instruction was set in the template" would be both wrong and
        useless for exactly the task that most needs telling.
      */}
      <p className="why">
        {task.instruction ??
          task.reviewReason ??
          task.blockedReason ??
          'No instruction was set on this step in the sequence template.'}
      </p>

      <label
        className="field"
        {...tip('Stored with the outcome you record below, so whoever picks this account up next can see what happened.')}
      >
        <span>Note (optional)</span>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <p className="variance-title">What happened?</p>
      {/* Pinned for the same reason as the approve row: recording the outcome
          is the whole point of a manual step, and the instruction plus the note
          box push these buttons past the fold on a short pane. */}
      <div className="draft-actions is-pinned">
        {/*
          The CRM verdict, inside the pinned bar and above the buttons for the
          same reason as the approve row: below a sticky row it never came back
          onto the screen, and "HubSpot refused" is the whole reason a rep would
          re-log the call by hand. Permanent region, changing contents — a live
          region mounted together with its text announces unreliably. Full-width
          so it takes its own line and the outcome buttons keep theirs.
        */}
        <div role="status" aria-live="polite" style={{ flexBasis: '100%' }}>
          {message && (
            <p className={message.kind === 'error' ? 'error-note' : 'ok-note'}>{message.text}</p>
          )}
        </div>

        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={busy !== null}
            className={option.value === 'meeting_booked' ? 'primary' : ''}
            {...tip(
              option.value === 'meeting_booked'
                ? 'Completes this step and holds every remaining step for this contact. Reversible — resuming sales contact from their card puts the held steps back.'
                : option.value === 'not_interested'
                  ? 'Completes this step and stops every remaining step for this contact.'
                  : 'Records this outcome and completes the step. The rest of the sequence carries on as scheduled.',
            )}
            onClick={() => void complete(option.value)}
          >
            {busy === option.value ? 'Saving…' : option.label}
          </button>
        ))}

        {/*
          Snooze and Skip, which a manual step never had — §15's action list
          and the mockup's own buttons.

          A call step is exactly the kind of work that gets deferred: the
          prospect is in a meeting, or it is the wrong hour in their country.
          Without these, a rep's only options were to log an outcome that did
          not happen or to leave the row sitting in the queue all week, and
          both are worse than saying "not today". The endpoints are the same
          ones the draft pane uses — nothing here is a second code path.
        */}
        <button
          type="button"
          disabled={busy !== null}
          {...tip(
            'Parks this step in Not sending for three days. It comes back to the queue then, and the rest of the sequence keeps its schedule.',
          )}
          onClick={() => void defer()}
        >
          {busy === 'Snooze' ? 'Snoozing…' : 'Snooze 3d'}
        </button>

        <button
          type="button"
          disabled={busy !== null}
          {...tipAt(
            'Abandons this step without recording an outcome. Later steps still run. Cannot be undone from here.',
            'end',
          )}
          onClick={() => void abandon()}
        >
          {busy === 'Skip' ? 'Skipping…' : 'Skip'}
        </button>
      </div>

      <p className="fine">
        Booking a meeting holds the remaining steps until someone resumes sales contact for them;
        a &ldquo;not interested&rdquo; stops them for good. Everything else just completes this one.
      </p>

      {/* §17.5 names two triggers for Find contacts: the account drawer, and
          THIS — the §6.3 research task. Same panel, same caps, same review
          step; the account payload it loads applies the §8.1.1 visibility
          rules, so a task on someone else's account cannot become a side door. */}
      {task.stepType === 'research' && task.accountId !== null && (
        <ResearchProspecting accountId={task.accountId} onChanged={() => void onChanged()} />
      )}
    </div>
  );
}

/**
 * The research-task Find-contacts launcher — §17.5's second trigger.
 *
 * Collapsed until asked for: most research steps are "confirm this person's
 * address", and unfurling a search surface under all of them would bury the
 * note box that answers those. Expanding fetches the account (which is where
 * availability, ownership and the connector state are decided) and mounts the
 * same ProspectPanel the drawer uses.
 */
function ResearchProspecting({
  accountId,
  onChanged,
}: {
  accountId: number;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<{
    available: boolean;
    reason: string | null;
    note?: string;
    vendor?: VendorDescriptor;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The launcher is collapsed and has no account payload yet, so the name on
  // the button comes from the cached descriptor (one fetch per tab, ever).
  const { vendor } = useVendor();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api<{
      prospecting: { available: boolean; reason: string | null; note?: string };
      vendor?: VendorDescriptor;
    }>(`/accounts/${accountId}`)
      .then((d) => {
        if (!cancelled) setState({ ...d.prospecting, vendor: d.vendor });
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [open, accountId]);

  if (!open) {
    // Never absent because a fetch is slow: "Find contacts" with no
    // parenthetical is honest, and the panel it opens loads the vendor.
    return (
      <div className="draft-actions">
        <button
          type="button"
          {...tip(
            vendor
              ? `Opens the same Find-contacts flow as the account drawer on ${vendor.displayName}: ${vendor.pricing.search} A human reviews before anything is revealed.`
              : 'Opens the same Find-contacts flow as the account drawer. A human reviews before anything is revealed.',
          )}
          onClick={() => setOpen(true)}
        >
          {vendor ? `Find contacts (${vendor.displayName})` : 'Find contacts'}
        </button>
      </div>
    );
  }

  if (error) return <p className="error-note">{error}</p>;
  if (!state) return <p className="fine">Checking prospecting availability&hellip;</p>;

  return (
    <ProspectPanel
      accountId={accountId}
      available={state.available}
      reason={state.reason}
      note={state.note}
      vendor={state.vendor ?? vendor}
      onChanged={onChanged}
    />
  );
}
